import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { UnlockPlansModal, useBodyScrollLock } from '@/components/UnlockPlansModal';
import { goAuth } from '@/components/ContentGate';
import { ReviewGate, GuestResultModal } from '@/components/ReviewGate';
import { ResultHero } from '@/components/ResultHero';
import { SubscribeNudge } from '@/components/SubscribeNudge';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { UserPlus, FileQuestion, CheckCircle, XCircle, RotateCcw, ArrowRight, ArrowLeft, Trophy, Clock, Eye, Crown, X, Flag, Lock, Share2, Copy, Check, Download, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import T from '@/components/StyledTranslation';
import { BackButton } from '@/components/BackButton';
import SEO from '@/components/SEO';
import html2canvas from 'html2canvas';
import { trackTestStart, trackTestComplete, trackPaywallShown, trackTestAbandoned, trackShareResult } from '@/utils/analytics';
import { FAQSchema, QuizSchema, PageBreadcrumbs } from '@/components/StructuredData';
import RelatedPages from '@/components/RelatedPages';
import VisibleBreadcrumbs from '@/components/VisibleBreadcrumbs';
import { API, API_URL } from '@/config';
import { getPrefetched, getResolvedData, prefetchDetail } from '@/utils/dataPrefetcher';
import GoogleReviewPrompt, { useGoogleReviewPrompt } from '@/components/GoogleReviewPrompt';
import { speak, stopSpeaking, isTTSSupported } from '@/utils/tts';
import { SpeakButton } from '@/components/SpeakButton';

// Visitor ID for analytics
const getVisitorId = () => {
  let vid = localStorage.getItem('dm_visitor_id');
  if (!vid) {
    vid = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('dm_visitor_id', vid);
  }
  return vid;
};
const getSessionId = () => {
  let sid = sessionStorage.getItem('dm_session_id');
  if (!sid) {
    sid = 's_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('dm_session_id', sid);
  }
  return sid;
};

// Multi-language text helper - supports all 9 languages
const getText = (texts) => {
  const lang = localStorage.getItem('i18nextLng') || 'sv';
  return texts[lang] || texts.en || texts.sv || Object.values(texts)[0];
};

// Helper to get options array in the correct format
// Handles both formats: array of objects with textSv/textAr/textEn OR object with sv/en/ar arrays
const getOptionsForLanguage = (options, lang) => {
  if (!options) return [];

  // Helper: returns the list if it's a non-empty array of non-empty strings
  const usable = (arr) => Array.isArray(arr) && arr.length > 0 && arr.every(s => typeof s === 'string' && s.trim() !== '');

  // Format 1: Object with language keys pointing to arrays {sv: [...], en: [...]}
  if (typeof options === 'object' && !Array.isArray(options)) {
    // Try requested language first
    if (usable(options[lang])) return options[lang];
    // Fallback chain: sv → en → ar → any other usable language
    const fallbackOrder = ['sv', 'en', 'ar', 'de', 'tr', 'fa', 'es', 'pl', 'so'];
    for (const code of fallbackOrder) {
      if (code !== lang && usable(options[code])) return options[code];
    }
    // Last resort: any usable array we can find
    for (const key of Object.keys(options)) {
      if (usable(options[key])) return options[key];
    }
    return [];
  }

  // Format 2: Array of objects with textSv, textAr, textEn, etc.
  if (Array.isArray(options)) {
    const langKey = `text${lang.charAt(0).toUpperCase()}${lang.slice(1)}`; // e.g., textSv, textAr
    return options.map(opt => {
      if (typeof opt === 'string') return opt;
      return opt[langKey] || opt.textSv || opt.textEn || opt.textAr || opt.text || '';
    });
  }

  return [];
};

// Helper to get question text in the correct format
// Handles both formats: object with language keys OR string with separate questionSv/questionAr fields
const getQuestionText = (question, lang) => {
  if (!question) return '';

  // Helper: returns text if it's a non-empty string
  const usable = (s) => typeof s === 'string' && s.trim() !== '';

  // Format 1: question is an object with language keys {sv: "...", en: "...", ar: "..."}
  if (typeof question.question === 'object' && question.question !== null) {
    const q = question.question;
    if (usable(q[lang])) return q[lang];
    // Fallback chain: sv → en → ar → any other usable language
    const fallbackOrder = ['sv', 'en', 'ar', 'de', 'tr', 'fa', 'es', 'pl', 'so'];
    for (const code of fallbackOrder) {
      if (code !== lang && usable(q[code])) return q[code];
    }
    for (const key of Object.keys(q)) {
      if (usable(q[key])) return q[key];
    }
    return '';
  }

  // Format 2: question is a string, check for separate questionSv/questionAr/questionEn fields
  const langKey = `question${lang.charAt(0).toUpperCase()}${lang.slice(1)}`; // e.g., questionSv, questionAr
  if (usable(question[langKey])) return question[langKey];

  // Fallback to questionSv or questionEn or just question (string)
  return question.questionSv || question.questionEn || question.questionAr || question.question || '';
};

// Translate test name like "Theory Test 1" to user's language.
// For Taxi tests we render a category-aware short label so they're visually
// distinct from B-körkort cards even though both flows share the same grid.
const getTestName = (testName, testIndex, test) => {
  const lang = localStorage.getItem('i18nextLng') || 'sv';
  const theoryTestTranslations = {
    en: 'Theory Test', sv: 'Teoriprov', ar: 'اختبار نظري', tr: 'Teori Testi', 
    fa: 'آزمون تئوری', so: 'Imtixaanka', pl: 'Test teoretyczny', es: 'Test de teoría', de: 'Theorietest'
  };

  // Taxi-specific naming: extract the trailing number and prefix with the
  // sub-category so the card reads e.g. "Säkerhet 1" / "Lag. 1" / "Slutprov 1"
  const isTaxi = (test?.licenseType || '').toLowerCase() === 'taxi';
  if (isTaxi) {
    const raw = (typeof testName === 'string' ? testName : (testName?.[lang] || testName?.sv || testName?.en || '')) || '';
    const num = raw.match(/(\d+)\s*$/)?.[1] || String(testIndex || '');
    const isFinal = !!test?.isFinalExam;
    const cat = (test?.taxiCategory || '').toLowerCase();
    const shortAr = cat === 'lagstiftning' ? 'تشريعات' : 'الأمان';
    const shortSv = cat === 'lagstiftning' ? 'Lagstift.' : 'Säkerhet';
    const base = lang === 'ar' ? shortAr : shortSv;
    const kind = isFinal
      ? (lang === 'ar' ? 'نهائي' : 'Slutprov')
      : '';
    return kind ? `${base} ${kind} ${num}` : `${base} ${num}`;
  }
  
  // If testName already contains translations object
  if (typeof testName === 'object' && testName !== null) {
    return testName[lang] || testName.sv || testName.en || `${theoryTestTranslations[lang] || theoryTestTranslations.en} ${testIndex}`;
  }
  
  // If testName is a string like "Theory Test 1"
  if (typeof testName === 'string') {
    const match = testName.match(/(\d+)$/);
    if (match) {
      const number = match[1];
      return `${theoryTestTranslations[lang] || theoryTestTranslations.en} ${number}`;
    }
    // If no number found, just return the theory test translation with index
    return `${theoryTestTranslations[lang] || theoryTestTranslations.en} ${testIndex || ''}`;
  }
  
  return `${theoryTestTranslations[lang] || theoryTestTranslations.en} ${testIndex || ''}`;
};

// Common translations for TestsPage
const TEXTS = {
  theoryTest: { en: 'Theory Test', sv: 'Teoriprov', ar: 'اختبار نظري', tr: 'Teori Testi', fa: 'آزمون تئوری', so: 'Imtixaanka Aragtida', pl: 'Test teoretyczny', es: 'Test de teoría', de: 'Theorietest' },
  oneMinuteRemaining: { en: '⏰ Only 1 minute remaining!', sv: '⏰ Endast en minut kvar!', ar: '⏰ تبقى دقيقة واحدة فقط!', tr: '⏰ Sadece 1 dakika kaldı!', fa: '⏰ فقط یک دقیقه باقی مانده!', so: '⏰ Hal daqiiqo oo kaliya ayaa hadhay!', pl: '⏰ Została tylko 1 minuta!', es: '⏰ ¡Solo queda 1 minuto!', de: '⏰ Nur noch 1 Minute!' },
  timeUp: { en: '🚨 Time is up! Test will be submitted automatically...', sv: '🚨 Tiden är slut! Provet skickas automatiskt...', ar: '🚨 انتهى الوقت! سيتم إرسال الاختبار تلقائياً...', tr: '🚨 Süre doldu! Test otomatik olarak gönderilecek...', fa: '🚨 زمان تمام شد! آزمون به صورت خودکار ارسال می‌شود...', so: '🚨 Waqtigii wuu dhamaaday! Imtixaanka waa la soo diri doonaa...', pl: '🚨 Czas minął! Test zostanie wysłany automatycznie...', es: '🚨 ¡Se acabó el tiempo! El test se enviará automáticamente...', de: '🚨 Zeit abgelaufen! Der Test wird automatisch abgeschickt...' },
  warningLeaveTest: { en: 'Are you sure you want to leave? Your progress will be lost.', sv: 'Är du säker på att du vill lämna? Dina framsteg kommer att gå förlorade.', ar: 'هل أنت متأكد أنك تريد المغادرة؟ سيتم فقدان تقدمك.', tr: 'Ayrılmak istediğinizden emin misiniz? İlerlemeniz kaybolacak.', fa: 'آیا مطمئن هستید که می‌خواهید خارج شوید؟ پیشرفت شما از دست خواهد رفت.', so: 'Ma hubtaa inaad rabto inaad tagto? Horummarkaaga wuu lumi doonaa.', pl: 'Czy na pewno chcesz wyjść? Twój postęp zostanie utracony.', es: '¿Estás seguro de que quieres salir? Tu progreso se perderá.', de: 'Sind Sie sicher, dass Sie gehen möchten? Ihr Fortschritt geht verloren.' },
  testInProgress: { en: 'Test in Progress', sv: 'Prov pågår', ar: 'الاختبار قيد التقدم', tr: 'Test Devam Ediyor', fa: 'آزمون در حال انجام', so: 'Imtixaanka waa socda', pl: 'Test w trakcie', es: 'Test en progreso', de: 'Test läuft' },
  submitTest: { en: 'Submit Test', sv: 'Skicka provet', ar: 'إرسال الاختبار', tr: 'Testi Gönder', fa: 'ارسال آزمون', so: 'Dir Imtixaanka', pl: 'Wyślij test', es: 'Enviar test', de: 'Test abschicken' },
  cancel: { en: 'Cancel', sv: 'Avbryt', ar: 'إلغاء', tr: 'İptal', fa: 'لغو', so: 'Ka noqo', pl: 'Anuluj', es: 'Cancelar', de: 'Abbrechen' },
  stay: { en: 'Stay', sv: 'Stanna', ar: 'البقاء', tr: 'Kal', fa: 'بمان', so: 'Joog', pl: 'Zostań', es: 'Quedarse', de: 'Bleiben' },
  leave: { en: 'Leave', sv: 'Lämna', ar: 'مغادرة', tr: 'Ayrıl', fa: 'خروج', so: 'Tag', pl: 'Wyjdź', es: 'Salir', de: 'Verlassen' },
  checking: { en: 'Checking...', sv: 'Kontrollerar...', ar: 'جاري التحقق...', tr: 'Kontrol ediliyor...', fa: 'در حال بررسی...', so: 'Waa la hubinayaa...', pl: 'Sprawdzanie...', es: 'Comprobando...', de: 'Überprüfung...' },
  min: { en: 'min', sv: 'min', ar: 'دقيقة', tr: 'dk', fa: 'دقیقه', so: 'daqiiqo', pl: 'min', es: 'min', de: 'Min' },
  pass: { en: 'pass', sv: 'godkänt', ar: 'للنجاح', tr: 'geçme', fa: 'قبولی', so: 'guul', pl: 'zaliczenie', es: 'aprobar', de: 'bestehen' },
  chooseTest: { en: '📋 Choose a Test', sv: '📋 Välj ett test', ar: '📋 اختر اختباراً', tr: '📋 Bir Test Seçin', fa: '📋 یک آزمون انتخاب کنید', so: '📋 Dooro Imtixaan', pl: '📋 Wybierz test', es: '📋 Elige un test', de: '📋 Wählen Sie einen Test' },
  badgePractice: { en: 'Practice', sv: 'Övning', ar: 'تدريب', tr: 'Pratik', fa: 'تمرین', so: 'Tababar', pl: 'Praktyka', es: 'Práctica', de: 'Übung' },
  badgeFinal: { en: 'Final', sv: 'Slutprov', ar: 'نهائي', tr: 'Final', fa: 'نهایی', so: 'Imtixaan', pl: 'Finał', es: 'Final', de: 'Abschluss' },
  gotoPractice: { en: 'Go to practice tests', sv: 'Gå till övningsprov', ar: 'انتقل إلى التدريب', tr: 'Pratik testlere git', fa: 'به آزمون‌های تمرینی بروید', so: 'U gudub imtixaanada tababarka', pl: 'Przejdź do testów praktycznych', es: 'Ir a tests de práctica', de: 'Zu Übungstests gehen' },
  gotoFinal: { en: 'Go to final exams', sv: 'Gå till slutprov', ar: 'انتقل إلى الامتحان النهائي', tr: 'Final sınavlarına git', fa: 'به آزمون‌های نهایی بروید', so: 'U gudub imtixaanada dhamaadka', pl: 'Przejdź do egzaminów końcowych', es: 'Ir a exámenes finales', de: 'Zu Abschlussprüfungen gehen' },
  finalExamTitleEmoji: { en: '🎯 Final Exam', sv: '🎯 Slutprov', ar: '🎯 الامتحان النهائي', tr: '🎯 Final Sınavı', fa: '🎯 آزمون نهایی', so: '🎯 Imtixaanka Dhamaadka', pl: '🎯 Egzamin Końcowy', es: '🎯 Examen Final', de: '🎯 Abschlussprüfung' },
  finalExamSubtitle: { en: '{n} questions • 50 min • {p} correct to pass', sv: '{n} frågor • 50 min • {p} rätt för godkänt', ar: '{n} سؤالاً • 50 دقيقة • {p} إجابة صحيحة للنجاح', tr: '{n} soru • 50 dk • geçmek için {p} doğru', fa: '{n} سوال • ۵۰ دقیقه • {p} پاسخ درست برای قبولی', so: "{n} su'aalood • 50 daqiiqo • {p} sax ah si aad u gudubto", pl: '{n} pytań • 50 min • {p} poprawnych, aby zdać', es: '{n} preguntas • 50 min • {p} correctas para aprobar', de: '{n} Fragen • 50 Min • {p} richtig zum Bestehen' },
  examsCount: { en: 'exams', sv: 'prov', ar: 'بطاقات', tr: 'sınav', fa: 'آزمون', so: 'imtixaano', pl: 'egzaminów', es: 'exámenes', de: 'Prüfungen' },
  finalExamBelow: { en: 'Final Exam Below', sv: 'Slutprov nedan', ar: 'الامتحان النهائي بالأسفل', tr: 'Final Sınavı Aşağıda', fa: 'آزمون نهایی در پایین', so: 'Imtixaanka Dhamaadka Hoosta', pl: 'Egzamin Końcowy Poniżej', es: 'Examen Final Abajo', de: 'Abschlussprüfung Unten' },
  finalExamHeader: { en: 'Final Exam', sv: 'Slutprov', ar: 'الامتحان النهائي', tr: 'Final Sınavı', fa: 'آزمون نهایی', so: 'Imtixaanka Dhamaadka', pl: 'Egzamin Końcowy', es: 'Examen Final', de: 'Abschlussprüfung' },
  finalExamTagline: { en: '📜 ⭐ Test your level before the theory test — {n} questions ⭐ 📜', sv: '📜 ⭐ Testa din nivå inför kunskapsprovet — {n} frågor ⭐ 📜', ar: '📜 ⭐ اختبر مستواك قبل الامتحان النظري — {n} سؤالاً ⭐ 📜', tr: '📜 ⭐ Teori sınavından önce seviyeni test et — {n} soru ⭐ 📜', fa: '📜 ⭐ سطح خود را قبل از آزمون تئوری بسنجید — {n} سوال ⭐ 📜', so: "📜 ⭐ Tijaabi heerkaaga ka hor imtixaanka aragtida — {n} su'aalood ⭐ 📜", pl: '📜 ⭐ Sprawdź swój poziom przed egzaminem teoretycznym — {n} pytań ⭐ 📜', es: '📜 ⭐ Pon a prueba tu nivel antes del examen teórico — {n} preguntas ⭐ 📜', de: '📜 ⭐ Teste dein Niveau vor der Theorieprüfung — {n} Fragen ⭐ 📜' },
  examLabel: { en: 'Exam', sv: 'Prov', ar: 'اختبار', tr: 'Sınav', fa: 'آزمون', so: 'Imtixaan', pl: 'Egzamin', es: 'Examen', de: 'Prüfung' },
  examSimDesc: { en: 'Complete preparation for the theory test', sv: 'Fullständig förberedelse för kunskapsprovet', ar: 'تحضير كامل لاختبار المعرفة النظرية', tr: 'Teori sınavına tam hazırlık', fa: 'آمادگی کامل برای آزمون تئوری', so: 'Diyaargarow buuxda imtixaanka aragtieed', pl: 'Pełne przygotowanie do teoretycznego egzaminu', es: 'Preparación completa para el examen teórico', de: 'Vollständige Vorbereitung auf die Theorieprüfung' },
  scrollDown: { en: 'Scroll down', sv: 'Scrolla ner', ar: 'مرر للمزيد', tr: 'Aşağı kaydır', fa: 'به پایین بروید', so: 'Hoos u dhaadhac', pl: 'Przewiń w dół', es: 'Desplázate hacia abajo', de: 'Nach unten scrollen' },
  loading: { en: 'Loading...', sv: 'Laddar...', ar: 'جاري التحميل...', tr: 'Yükleniyor...', fa: 'در حال بارگذاری...', so: 'Waa la soo geliyaa...', pl: 'Ładowanie...', es: 'Cargando...', de: 'Laden...' },
  noTests: { en: 'No tests available yet', sv: 'Inga tester tillgängliga ännu', ar: 'لا توجد اختبارات متاحة حالياً', tr: 'Henüz test bulunmuyor', fa: 'هنوز آزمونی موجود نیست', so: 'Wali imtixaano ma jiraan', pl: 'Brak dostępnych testów', es: 'No hay tests disponibles', de: 'Noch keine Tests verfügbar' },
  questions: { en: 'Q', sv: 'F', ar: 'س', tr: 'S', fa: 'س', so: 'S', pl: 'P', es: 'P', de: 'F' },
  minutes: { en: 'm', sv: 'm', ar: 'د', tr: 'dk', fa: 'د', so: 'd', pl: 'm', es: 'm', de: 'M' },
  total: { en: 'Total', sv: 'Totalt', ar: 'إجمالي', tr: 'Toplam', fa: 'مجموع', so: 'Wadarta', pl: 'Razem', es: 'Total', de: 'Gesamt' },
  tests: { en: 'tests', sv: 'tester', ar: 'اختبار', tr: 'test', fa: 'آزمون', so: 'imtixaanno', pl: 'testów', es: 'tests', de: 'Tests' },
  subscribeToContiue: { en: 'Subscribe to Continue', sv: 'Prenumerera för att fortsätta', ar: 'اشترك للمتابعة', tr: 'Devam etmek için abone olun', fa: 'برای ادامه اشتراک بگیرید', so: 'Isdiiwaangeli si aad u sii wadato', pl: 'Subskrybuj, aby kontynuować', es: 'Suscríbete para continuar', de: 'Abonnieren Sie, um fortzufahren' },
  backToCards: { en: 'Back to Cards', sv: 'Tillbaka till korten', ar: 'العودة للبطاقات', tr: 'Kartlara Dön', fa: 'بازگشت به کارت‌ها', so: 'Ku noqo Kaararka', pl: 'Powrót do kart', es: 'Volver a las tarjetas', de: 'Zurück zu den Karten' },
  reviewAnswers: { en: '📋 Review Answers', sv: '📋 Granska svar', ar: '📋 مراجعة الإجابات', tr: '📋 Cevapları Gözden Geçir', fa: '📋 مرور پاسخ‌ها', so: '📋 Dib u eeg Jawaabaha', pl: '📋 Przejrzyj odpowiedzi', es: '📋 Revisar respuestas', de: '📋 Antworten überprüfen' },
  correct: { en: '✓ Correct', sv: '✓ Rätt', ar: '✓ إجابة صحيحة', tr: '✓ Doğru', fa: '✓ صحیح', so: '✓ Sax', pl: '✓ Poprawne', es: '✓ Correcto', de: '✓ Richtig' },
  incorrect: { en: '✗ Incorrect', sv: '✗ Fel', ar: '✗ إجابة خاطئة', tr: '✗ Yanlış', fa: '✗ غلط', so: '✗ Qalad', pl: '✗ Błędne', es: '✗ Incorrecto', de: '✗ Falsch' },
  unanswered: { en: 'Unanswered', sv: 'Obesvarad', ar: 'بدون إجابة', tr: 'Cevaplanmadı', fa: 'پاسخ داده نشده', so: 'Lama jawaabin', pl: 'Bez odpowiedzi', es: 'Sin responder', de: 'Unbeantwortet' },
  explanation: { en: 'Explanation', sv: 'Förklaring', ar: 'التفسير', tr: 'Açıklama', fa: 'توضیح', so: 'Sharaxaad', pl: 'Wyjaśnienie', es: 'Explicación', de: 'Erklärung' },
  correctAnswer: { en: 'Correct answer', sv: 'Rätt svar', ar: 'الإجابة الصحيحة', tr: 'Doğru cevap', fa: 'پاسخ صحیح', so: 'Jawaabta saxda ah', pl: 'Poprawna odpowiedź', es: 'Respuesta correcta', de: 'Richtige Antwort' },
  yourAnswer: { en: 'Your answer', sv: 'Ditt svar', ar: 'إجابتك', tr: 'Cevabınız', fa: 'پاسخ شما', so: 'Jawaabtaada', pl: 'Twoja odpowiedź', es: 'Tu respuesta', de: 'Ihre Antwort' },
  testResults: { en: '🎯 Test Results', sv: '🎯 Testresultat', ar: '🎯 نتائج الاختبار', tr: '🎯 Test Sonuçları', fa: '🎯 نتایج آزمون', so: '🎯 Natiijooyinka Imtixaanka', pl: '🎯 Wyniki testu', es: '🎯 Resultados del test', de: '🎯 Testergebnisse' },
  congratulations: { en: '🎉 Congratulations! You passed!', sv: '🎉 Grattis! Du klarade det!', ar: '🎉 تهانينا! لقد نجحت!', tr: '🎉 Tebrikler! Geçtiniz!', fa: '🎉 تبریک! شما قبول شدید!', so: '🎉 Hambalyo! Waad guuleysatay!', pl: '🎉 Gratulacje! Zdałeś!', es: '🎉 ¡Felicidades! ¡Aprobaste!', de: '🎉 Herzlichen Glückwunsch! Sie haben bestanden!' },
  keepPracticing: { en: 'Keep practicing!', sv: 'Fortsätt öva!', ar: 'استمر في التدريب!', tr: 'Pratik yapmaya devam edin!', fa: 'به تمرین ادامه دهید!', so: 'Sii wad tababarka!', pl: 'Ćwicz dalej!', es: '¡Sigue practicando!', de: 'Weiter üben!' },
  tryAgain: { en: 'Try Again', sv: 'Försök igen', ar: 'حاول مرة أخرى', tr: 'Tekrar Dene', fa: 'دوباره امتحان کنید', so: 'Mar kale isku day', pl: 'Spróbuj ponownie', es: 'Intentar de nuevo', de: 'Erneut versuchen' },
  backToTests: { en: 'Back to Tests', sv: 'Tillbaka till tester', ar: 'العودة للاختبارات', tr: 'Testlere Dön', fa: 'بازگشت به آزمون‌ها', so: 'Ku noqo Imtixaanada', pl: 'Powrót do testów', es: 'Volver a los tests', de: 'Zurück zu den Tests' },
  score: { en: 'Score', sv: 'Poäng', ar: 'النتيجة', tr: 'Puan', fa: 'نمره', so: 'Dhibcaha', pl: 'Wynik', es: 'Puntuación', de: 'Punktzahl' },
  passed: { en: 'PASSED', sv: 'GODKÄND', ar: 'ناجح', tr: 'GEÇTİ', fa: 'قبول', so: 'GUULEYSTAY', pl: 'ZDAŁ', es: 'APROBADO', de: 'BESTANDEN' },
  failed: { en: 'FAILED', sv: 'UNDERKÄND', ar: 'راسب', tr: 'KALDI', fa: 'مردود', so: 'DHACAY', pl: 'NIE ZDAŁ', es: 'SUSPENDIDO', de: 'NICHT BESTANDEN' },
  requiredToPass: { en: 'Required to pass', sv: 'Krävs för godkänt', ar: 'مطلوب للنجاح', tr: 'Geçmek için gerekli', fa: 'برای قبولی لازم است', so: 'Loo baahan yahay si loo guuleysto', pl: 'Wymagane do zaliczenia', es: 'Requerido para aprobar', de: 'Erforderlich zum Bestehen' },
  startTest: { en: 'Start Test', sv: 'Starta test', ar: 'ابدأ الاختبار', tr: 'Teste Başla', fa: 'شروع آزمون', so: 'Bilow Imtixaanka', pl: 'Rozpocznij test', es: 'Iniciar test', de: 'Test starten' },
  question: { en: 'Question', sv: 'Fråga', ar: 'سؤال', tr: 'Soru', fa: 'سوال', so: 'Su\'aal', pl: 'Pytanie', es: 'Pregunta', de: 'Frage' },
  of: { en: 'of', sv: 'av', ar: 'من', tr: '/', fa: 'از', so: 'ka mid ah', pl: 'z', es: 'de', de: 'von' },
  timeRemaining: { en: 'Time remaining', sv: 'Tid kvar', ar: 'الوقت المتبقي', tr: 'Kalan süre', fa: 'زمان باقیمانده', so: 'Waqtiga hadhay', pl: 'Pozostały czas', es: 'Tiempo restante', de: 'Verbleibende Zeit' },
  next: { en: 'Next', sv: 'Nästa', ar: 'التالي', tr: 'Sonraki', fa: 'بعدی', so: 'Xiga', pl: 'Następne', es: 'Siguiente', de: 'Weiter' },
  previous: { en: 'Previous', sv: 'Föregående', ar: 'السابق', tr: 'Önceki', fa: 'قبلی', so: 'Hore', pl: 'Poprzednie', es: 'Anterior', de: 'Zurück' },
  finish: { en: 'Finish', sv: 'Avsluta', ar: 'إنهاء', tr: 'Bitir', fa: 'پایان', so: 'Dhamee', pl: 'Zakończ', es: 'Finalizar', de: 'Beenden' },
  confirmFinish: { en: '⚠️ Confirm Finish', sv: '⚠️ Bekräfta avslut', ar: '⚠️ تأكيد إنهاء الاختبار', tr: '⚠️ Bitirmeyi Onayla', fa: '⚠️ تأیید پایان', so: '⚠️ Xaqiiji Dhamaystirka', pl: '⚠️ Potwierdź zakończenie', es: '⚠️ Confirmar finalización', de: '⚠️ Beenden bestätigen' },
  questionsLeft: { en: 'questions left', sv: 'frågor kvar', ar: 'سؤال متبقي', tr: 'soru kaldı', fa: 'سوال باقی مانده', so: "su'aalood oo hadhay", pl: 'pytań pozostało', es: 'preguntas restantes', de: 'Fragen übrig' },
  answered: { en: 'Answered', sv: 'Besvarad', ar: 'مُجاب', tr: 'Cevaplandı', fa: 'پاسخ داده شده', so: 'La jawaabay', pl: 'Odpowiedziano', es: 'Respondidas', de: 'Beantwortet' },
  unanswered: { en: 'Unanswered', sv: 'Obesvarad', ar: 'لم يُجب', tr: 'Cevaplanmadı', fa: 'پاسخ داده نشده', so: 'Aan la jawaabin', pl: 'Bez odpowiedzi', es: 'Sin responder', de: 'Unbeantwortet' },
  left: { en: 'left', sv: 'kvar', ar: 'متبقي', tr: 'kaldı', fa: 'باقی مانده', so: 'hadhay', pl: 'pozostało', es: 'restante', de: 'übrig' },
  confirmFinishQuestion: { en: 'Are you sure you want to finish the test?', sv: 'Är du säker på att du vill avsluta provet?', ar: 'هل أنت متأكد من أنك تريد إنهاء الاختبار؟', tr: 'Testi bitirmek istediğinizden emin misiniz?', fa: 'آیا مطمئن هستید که می‌خواهید آزمون را تمام کنید؟', so: 'Ma hubtaa inaad rabto inaad dhamaystirto imtixaanka?', pl: 'Czy na pewno chcesz zakończyć test?', es: '¿Estás seguro de que quieres finalizar el test?', de: 'Sind Sie sicher, dass Sie den Test beenden möchten?' },
  answeredQuestions: { en: 'Answered questions:', sv: 'Besvarade frågor:', ar: 'الأسئلة المُجابة:', tr: 'Cevaplanan sorular:', fa: 'سوالات پاسخ داده شده:', so: "Su'aalaha la jawaabay:", pl: 'Pytania z odpowiedzią:', es: 'Preguntas respondidas:', de: 'Beantwortete Fragen:' },
  unansweredQuestions: { en: 'Unanswered questions:', sv: 'Obesvarade frågor:', ar: 'أسئلة لم تُجب:', tr: 'Cevaplanmayan sorular:', fa: 'سوالات پاسخ داده نشده:', so: "Su'aalaha aan la jawaabin:", pl: 'Pytania bez odpowiedzi:', es: 'Preguntas sin responder:', de: 'Unbeantwortete Fragen:' },
  unansweredWillBeWrong: { en: '⚠️ Unanswered questions will be counted as wrong.', sv: '⚠️ Obesvarade frågor räknas som fel.', ar: '⚠️ الأسئلة غير المُجابة ستُحسب كإجابات خاطئة.', tr: '⚠️ Cevaplanmayan sorular yanlış sayılacaktır.', fa: '⚠️ سوالات پاسخ داده نشده به عنوان اشتباه محاسبه می‌شوند.', so: "⚠️ Su'aalaha aan la jawaabin waxaa loo tirin doonaa qalad.", pl: '⚠️ Pytania bez odpowiedzi zostaną policzone jako błędne.', es: '⚠️ Las preguntas sin responder se contarán como incorrectas.', de: '⚠️ Unbeantwortete Fragen werden als falsch gewertet.' },
  yesFinishTest: { en: 'Yes, finish test', sv: 'Ja, avsluta provet', ar: 'نعم، إنهاء الاختبار', tr: 'Evet, testi bitir', fa: 'بله، آزمون را تمام کن', so: 'Haa, dhamee imtixaanka', pl: 'Tak, zakończ test', es: 'Sí, finalizar test', de: 'Ja, Test beenden' },
  submitting: { en: 'Submitting...', sv: 'Skickar...', ar: 'جاري الإرسال...', tr: 'Gönderiliyor...', fa: 'در حال ارسال...', so: 'Waa la dirayaa...', pl: 'Wysyłanie...', es: 'Enviando...', de: 'Wird gesendet...' },
  warning: { en: '⚠️ Warning!', sv: '⚠️ Varning!', ar: '⚠️ تحذير!', tr: '⚠️ Uyarı!', fa: '⚠️ هشدار!', so: '⚠️ Digniin!', pl: '⚠️ Uwaga!', es: '⚠️ ¡Advertencia!', de: '⚠️ Warnung!' },
  explanation: { en: 'Explanation', sv: 'Förklaring', ar: 'التفسير', tr: 'Açıklama', fa: 'توضیح', so: 'Sharaxaad', pl: 'Wyjaśnienie', es: 'Explicación', de: 'Erklärung' },
  questionsTitle: { en: 'Questions', sv: 'Frågor', ar: 'الأسئلة', tr: 'Sorular', fa: 'سوالات', so: "Su'aalaha", pl: 'Pytania', es: 'Preguntas', de: 'Fragen' },
  legendNow: { en: 'Now', sv: 'Nu', ar: 'حالي', tr: 'Şimdi', fa: 'فعلی', so: 'Hadda', pl: 'Teraz', es: 'Ahora', de: 'Jetzt' },
  legendCorrect: { en: 'Correct', sv: 'Rätt', ar: 'صحيح', tr: 'Doğru', fa: 'صحیح', so: 'Sax', pl: 'Poprawne', es: 'Correcto', de: 'Richtig' },
  legendWrong: { en: 'Wrong', sv: 'Fel', ar: 'خطأ', tr: 'Yanlış', fa: 'غلط', so: 'Qalad', pl: 'Błędne', es: 'Incorrecto', de: 'Falsch' },
  legendTodo: { en: 'Todo', sv: 'Kvar', ar: 'باقي', tr: 'Yapılacak', fa: 'باقی', so: 'La sameeyo', pl: 'Do zrobienia', es: 'Pendiente', de: 'Ausstehend' },
  freeQuestionsFinished: { en: 'Free Questions Finished!', sv: 'Gratis frågor slut!', ar: 'انتهت الأسئلة المجانية!', tr: 'Ücretsiz sorular bitti!', fa: 'سوالات رایگان تمام شد!', so: "Su'aalaha bilaashka waa dhamaaday!", pl: 'Darmowe pytania skończone!', es: '¡Preguntas gratuitas terminadas!', de: 'Kostenlose Fragen beendet!' },
  freeQuestionsUsed: { en: "You've used your free questions. Subscribe now for unlimited access!", sv: 'Du har använt dina gratis frågor. Prenumerera nu för obegränsad tillgång!', ar: 'لقد استخدمت أسئلتك المجانية. اشترك الآن للوصول غير المحدود!', tr: 'Ücretsiz sorularınızı kullandınız. Sınırsız erişim için hemen abone olun!', fa: 'شما از سوالات رایگان خود استفاده کرده‌اید. برای دسترسی نامحدود اشتراک بگیرید!', so: "Waad isticmaashay su'aalaha bilaashka. Hadda isdiiwaangeli si aad u hesho gelitaan aan xaddidnayn!", pl: 'Wykorzystałeś darmowe pytania. Subskrybuj teraz, aby uzyskać nieograniczony dostęp!', es: 'Has usado tus preguntas gratuitas. ¡Suscríbete ahora para acceso ilimitado!', de: 'Sie haben Ihre kostenlosen Fragen verbraucht. Abonnieren Sie jetzt für unbegrenzten Zugang!' },
  shareResult: { en: 'Share Result', sv: 'Dela resultat', ar: 'شارك النتيجة', tr: 'Sonucu Paylaş', fa: 'اشتراک نتیجه', so: 'La wadaag natiijaada', pl: 'Udostępnij wynik', es: 'Compartir resultado', de: 'Ergebnis teilen' },
  copied: { en: 'Copied!', sv: 'Kopierat!', ar: 'تم النسخ!', tr: 'Kopyalandı!', fa: 'کپی شد!', so: 'La koobiyey!', pl: 'Skopiowano!', es: '¡Copiado!', de: 'Kopiert!' },
  shareText: { en: 'I scored {score}% on the Swedish driving theory test at DriveMaster! Try it yourself:', sv: 'Jag fick {score}% på teoriprovet hos DriveMaster! Testa själv:', ar: 'حصلت على {score}% في امتحان التيوري السويدي على DriveMaster! جرّب بنفسك:', tr: 'DriveMaster\'da teori sınavında {score}% aldım! Sen de dene:', fa: 'من {score}% در آزمون تئوری DriveMaster گرفتم! خودت هم امتحان کن:', so: 'Waxaan helay {score}% imtixaanka DriveMaster! Adigana tijaabi:', pl: 'Zdobyłem {score}% na teście DriveMaster! Spróbuj sam:', es: '¡Obtuve {score}% en el test DriveMaster! Pruébalo:', de: 'Ich habe {score}% beim DriveMaster-Test erreicht! Probier es selbst:' },
  subscribeNow: { en: 'Subscribe Now 🚀', sv: 'Prenumerera nu 🚀', ar: 'اشترك الآن 🚀', tr: 'Hemen Abone Ol 🚀', fa: 'اکنون اشتراک بگیرید 🚀', so: 'Hadda isdiiwaangeli 🚀', pl: 'Subskrybuj teraz 🚀', es: '¡Suscríbete ahora! 🚀', de: 'Jetzt abonnieren 🚀' },
  freePracticeTest: { en: 'Free Practice Test', sv: 'Gratis övningstest', ar: 'اختبار تدريبي مجاني', tr: 'Ücretsiz Pratik Testi', fa: 'آزمون تمرینی رایگان', so: 'Imtixaan Tababar Bilaash', pl: 'Darmowy test praktyczny', es: 'Test de práctica gratuito', de: 'Kostenloser Übungstest' },
  freeQuestions: { en: 'free questions', sv: 'gratis frågor', ar: 'سؤال مجاني', tr: 'ücretsiz soru', fa: 'سوال رایگان', so: "su'aalood bilaash ah", pl: 'darmowych pytań', es: 'preguntas gratis', de: 'kostenlose Fragen' },
  hideExplanation: { en: 'Hide Explanation', sv: 'Dölj förklaring', ar: 'إخفاء التفسير', tr: 'Açıklamayı Gizle', fa: 'مخفی کردن توضیح', so: 'Qari Sharaxaadda', pl: 'Ukryj wyjaśnienie', es: 'Ocultar explicación', de: 'Erklärung ausblenden' },
  showExplanation: { en: 'Show Explanation', sv: 'Visa förklaring', ar: 'عرض التفسير', tr: 'Açıklamayı Göster', fa: 'نمایش توضیح', so: 'Tus Sharaxaadda', pl: 'Pokaż wyjaśnienie', es: 'Mostrar explicación', de: 'Erklärung anzeigen' },
  loading: { en: 'Loading...', sv: 'Laddar...', ar: 'جاري التحميل...', tr: 'Yükleniyor...', fa: 'در حال بارگذاری...', so: 'Waa soo raraya...', pl: 'Ładowanie...', es: 'Cargando...', de: 'Laden...' },
  noTestsAvailable: { en: 'No tests available yet', sv: 'Inga prov tillgängliga ännu', ar: 'لا توجد اختبارات متاحة حالياً', tr: 'Henüz test yok', fa: 'هنوز آزمونی در دسترس نیست', so: 'Wali ma jiraan imtixaano', pl: 'Brak dostępnych testów', es: 'No hay tests disponibles', de: 'Noch keine Tests verfügbar' },
  questionShort: { en: 'Q', sv: 'F', ar: 'س', tr: 'S', fa: 'س', so: 'S', pl: 'P', es: 'P', de: 'F' },
  minuteShort: { en: 'm', sv: 'm', ar: 'د', tr: 'dk', fa: 'د', so: 'dq', pl: 'm', es: 'm', de: 'M' },
  totalTests: { en: 'Total:', sv: 'Totalt:', ar: 'إجمالي:', tr: 'Toplam:', fa: 'مجموع:', so: 'Wadarta:', pl: 'Razem:', es: 'Total:', de: 'Gesamt:' },
  tests: { en: 'tests', sv: 'tester', ar: 'اختبار', tr: 'test', fa: 'آزمون', so: 'imtixaan', pl: 'testów', es: 'tests', de: 'Tests' },
  subscribeToContinue: { en: 'Subscribe to Continue', sv: 'Prenumerera för att fortsätta', ar: 'اشترك للمتابعة', tr: 'Devam etmek için abone olun', fa: 'برای ادامه اشتراک بگیرید', so: 'Si aad u sii wadato isdiiwaangeli', pl: 'Subskrybuj, aby kontynuować', es: 'Suscríbete para continuar', de: 'Abonnieren zum Fortfahren' },
  quickNavigation: { en: 'Quick Navigation', sv: 'Snabbnavigering', ar: 'التنقل السريع', tr: 'Hızlı Gezinme', fa: 'پیمایش سریع', so: 'Hagaajinta Degdega', pl: 'Szybka nawigacja', es: 'Navegación rápida', de: 'Schnellnavigation' },
  clickAnyQuestion: { en: 'Click any question', sv: 'Klicka på fråga', ar: 'اضغط على أي سؤال', tr: 'Herhangi bir soruya tıklayın', fa: 'روی هر سوالی کلیک کنید', so: "Guji su'aal kasta", pl: 'Kliknij dowolne pytanie', es: 'Haz clic en cualquier pregunta', de: 'Klicken Sie auf eine Frage' },
  current: { en: 'Current', sv: 'Aktuell', ar: 'الحالي', tr: 'Mevcut', fa: 'فعلی', so: 'Hadda', pl: 'Bieżące', es: 'Actual', de: 'Aktuell' },
  currentProgress: { en: 'Current progress:', sv: 'Nuvarande framsteg:', ar: 'التقدم الحالي:', tr: 'Mevcut ilerleme:', fa: 'پیشرفت فعلی:', so: 'Horumarka hadda:', pl: 'Bieżący postęp:', es: 'Progreso actual:', de: 'Aktueller Fortschritt:' },
  timeRemaining: { en: 'Time remaining:', sv: 'Tid kvar:', ar: 'الوقت المتبقي:', tr: 'Kalan süre:', fa: 'زمان باقی‌مانده:', so: 'Waqtiga hadhay:', pl: 'Pozostały czas:', es: 'Tiempo restante:', de: 'Verbleibende Zeit:' },
  leaveTest: { en: 'Leave Test', sv: 'Lämna provet', ar: 'مغادرة الاختبار', tr: 'Testi Terk Et', fa: 'ترک آزمون', so: 'Ka bax Imtixaanka', pl: 'Opuść test', es: 'Abandonar test', de: 'Test verlassen' },
  stayAndContinue: { en: 'Stay and Continue', sv: 'Stanna och fortsätt', ar: 'البقاء ومتابعة الاختبار', tr: 'Kal ve Devam Et', fa: 'بمان و ادامه بده', so: 'Joog oo sii wad', pl: 'Zostań i kontynuuj', es: 'Quedarse y continuar', de: 'Bleiben und fortfahren' },
  trialNote: { en: '{n} trial questions are not scored · {p} correct needed to pass', sv: '{n} testfrågor räknas inte · {p} rätt krävs för godkänt', ar: '{n} أسئلة تجريبية لا تُحسب · تحتاج {p} إجابة صحيحة للنجاح', tr: '{n} deneme sorusu sayılmaz · geçmek için {p} doğru gerekir', fa: '{n} سوال آزمایشی محاسبه نمی‌شود · {p} پاسخ درست برای قبولی لازم است', so: "{n} su'aalood tijaabo ah lama xisaabiyo · {p} sax ah ayaa loo baahan yahay", pl: '{n} pytań próbnych się nie liczy · {p} poprawnych potrzeba, aby zdać', es: '{n} preguntas de prueba no puntúan · necesitas {p} correctas para aprobar', de: '{n} Testfragen werden nicht gewertet · {p} richtige zum Bestehen nötig' },
  trialQuestion: { en: 'Trial question – not scored', sv: 'Testfråga – räknas inte', ar: 'سؤال تجريبي – لا يُحسب', tr: 'Deneme sorusu – sayılmaz', fa: 'سوال آزمایشی – محاسبه نمی‌شود', so: "Su'aal tijaabo ah – lama xisaabiyo", pl: 'Pytanie próbne – nie liczy się', es: 'Pregunta de prueba – no puntúa', de: 'Testfrage – wird nicht gewertet' },
  freeBadge: { en: 'FREE', sv: 'GRATIS', ar: 'مجاني', tr: 'ÜCRETSİZ', fa: 'رایگان', so: 'BILAASH', pl: 'ZA DARMO', es: 'GRATIS', de: 'KOSTENLOS' },
  regBadge: { en: 'ACCOUNT', sv: 'KONTO', ar: 'بحساب', tr: 'HESAP', fa: 'با حساب', so: 'AKOON', pl: 'KONTO', es: 'CUENTA', de: 'KONTO' },
  regTitle: { en: 'Unlock test 2 for free', sv: 'Lås upp prov 2 gratis', ar: 'افتح الاختبار الثاني مجاناً', tr: 'Test 2\'yi ücretsiz aç', fa: 'آزمون ۲ را رایگان باز کنید', so: 'Imtixaanka 2 si bilaash ah u fur', pl: 'Odblokuj test 2 za darmo', es: 'Desbloquea el test 2 gratis', de: 'Test 2 kostenlos freischalten' },
  regDesc: { en: 'Sign up to DriveMaster and start the test right away.', sv: 'Registrera dig på DriveMaster och starta provet direkt.', ar: 'سجّل في DriveMaster وابدأ الاختبار فوراً.', tr: 'DriveMaster\'a kaydolun ve teste hemen başlayın.', fa: 'در DriveMaster ثبت‌نام کنید و آزمون را فوراً شروع کنید.', so: 'Iska diiwaangeli DriveMaster oo isla markiiba bilow imtixaanka.', pl: 'Zarejestruj się w DriveMaster i od razu zacznij test.', es: 'Regístrate en DriveMaster y empieza el test enseguida.', de: 'Registriere dich bei DriveMaster und starte den Test sofort.' },
  regCreate: { en: 'Sign up', sv: 'Registrera dig', ar: 'إنشاء حساب', tr: 'Kaydol', fa: 'ثبت‌نام', so: 'Is diiwaangeli', pl: 'Zarejestruj się', es: 'Registrarse', de: 'Registrieren' },
  regLogin: { en: 'I already have an account', sv: 'Jag har redan ett konto', ar: 'لدي حساب بالفعل', tr: 'Zaten hesabım var', fa: 'قبلاً حساب دارم', so: 'Horey ayaan akoon u leeyahay', pl: 'Mam już konto', es: 'Ya tengo una cuenta', de: 'Ich habe schon ein Konto' },
  lockedTitle: { en: 'This test is for subscribers', sv: 'Det här provet ingår i abonnemanget', ar: 'هذا الاختبار متاح للمشتركين', tr: 'Bu test abonelere özeldir', fa: 'این آزمون مخصوص مشترکین است', so: 'Imtixaankan waxaa loogu talagalay macaamiisha', pl: 'Ten test jest dostępny dla subskrybentów', es: 'Este examen es para suscriptores', de: 'Dieser Test ist für Abonnenten' },
  lockedDescB: { en: 'Your free questions are always available. To unlock all B-licence tests (800+ questions, 33 tests with full explanations), choose a B-körkort plan. The Taxi licence is sold separately.', sv: 'Dina gratisfrågor finns alltid tillgängliga. För att låsa upp alla prov för B-körkort (800+ frågor, 33 prov med förklaringar) väljer du ett paket för B-körkort. Taxikort säljs separat.', ar: 'أسئلتك المجانية متاحة لك دائماً. لفتح جميع اختبارات رخصة B (أكثر من 800 أسئلة و33 اختباراً مع شرح كامل) اشترك في باقة رخصة B. باقة التاكسي تُشترى بشكل منفصل.', tr: 'Ücretsiz sorularınız her zaman kullanılabilir. Tüm B ehliyeti testlerini açmak için (800+ soru, açıklamalı 33 test) bir B-körkort paketi seçin. Taksi ehliyeti ayrı satılır.', fa: 'سوالات رایگان شما همیشه در دسترس است. برای باز کردن همه آزمون‌های گواهینامه B (بیش از ۸۰۸ سوال و ۳۳ آزمون با توضیح کامل) بسته گواهینامه B را انتخاب کنید. بسته تاکسی جداگانه فروخته می‌شود.', so: 'Su\'aalahaaga bilaashka ah had iyo jeer waa diyaar. Si aad u furto dhammaan imtixaannada shatiga B (800+ su\'aalood, 33 imtixaan oo sharraxaad leh), dooro qorshe B-körkort. Shatiga Taksiga si gooni ah ayaa loo iibiyaa.', pl: 'Twoje darmowe pytania są zawsze dostępne. Aby odblokować wszystkie testy na kat. B (800+ pytań, 33 testy z wyjaśnieniami), wybierz pakiet B-körkort. Licencja taxi jest sprzedawana osobno.', es: 'Tus preguntas gratis siempre están disponibles. Para desbloquear todos los exámenes del permiso B (800+ preguntas, 33 exámenes con explicaciones), elige un plan B-körkort. La licencia de taxi se vende por separado.', de: 'Deine kostenlosen Fragen sind immer verfügbar. Um alle Tests für Führerschein B freizuschalten (800+ Fragen, 33 Tests mit Erklärungen), wähle ein B-körkort-Paket. Der Taxischein wird separat verkauft.' },
  lockedDescTaxi: { en: 'Your free questions are always available. To unlock all Taxi tests (480+ questions, 34 tests with full explanations), choose a Taxikort plan. The B licence is sold separately.', sv: 'Dina gratisfrågor finns alltid tillgängliga. För att låsa upp alla taxiprov (480+ frågor, 34 prov med förklaringar) väljer du ett paket för Taxikort. B-körkort säljs separat.', ar: 'أسئلتك المجانية متاحة لك دائماً. لفتح جميع اختبارات التاكسي (480+ سؤال و34 اختباراً مع شرح كامل) اشترك في باقة التاكسي. باقة رخصة B تُشترى بشكل منفصل.', tr: 'Ücretsiz sorularınız her zaman kullanılabilir. Tüm Taksi testlerini açmak için (480+ soru, açıklamalı 34 test) bir Taxikort paketi seçin. B ehliyeti ayrı satılır.', fa: 'سوالات رایگان شما همیشه در دسترس است. برای باز کردن همه آزمون‌های تاکسی (۴۵۲ سوال و ۳۴ آزمون با توضیح کامل) بسته تاکسی را انتخاب کنید. بسته گواهینامه B جداگانه فروخته می‌شود.', so: 'Su\'aalahaaga bilaashka ah had iyo jeer waa diyaar. Si aad u furto dhammaan imtixaannada Taksiga (480+ su\'aalood, 34 imtixaan oo sharraxaad leh), dooro qorshe Taxikort. Shatiga B si gooni ah ayaa loo iibiyaa.', pl: 'Twoje darmowe pytania są zawsze dostępne. Aby odblokować wszystkie testy taxi (480+ pytania, 34 testy z wyjaśnieniami), wybierz pakiet Taxikort. Prawo jazdy kat. B jest sprzedawane osobno.', es: 'Tus preguntas gratis siempre están disponibles. Para desbloquear todos los exámenes de taxi (480+ preguntas, 34 exámenes con explicaciones), elige un plan Taxikort. El permiso B se vende por separado.', de: 'Deine kostenlosen Fragen sind immer verfügbar. Um alle Taxi-Tests freizuschalten (480+ Fragen, 34 Tests mit Erklärungen), wähle ein Taxikort-Paket. Führerschein B wird separat verkauft.' },
  lockedF1B: { en: 'All B-licence tests', sv: 'Alla prov för B-körkort', ar: 'جميع اختبارات رخصة B', tr: 'Tüm B ehliyeti testleri', fa: 'همه آزمون‌های گواهینامه B', so: 'Dhammaan imtixaannada shatiga B', pl: 'Wszystkie testy na kat. B', es: 'Todos los exámenes del permiso B', de: 'Alle Tests für Führerschein B' },
  lockedF1Taxi: { en: 'All Taxi licence tests', sv: 'Alla prov för taxikort', ar: 'جميع اختبارات رخصة التاكسي', tr: 'Tüm Taksi ehliyeti testleri', fa: 'همه آزمون‌های گواهینامه تاکسی', so: 'Dhammaan imtixaannada shatiga Taksiga', pl: 'Wszystkie testy na licencję taxi', es: 'Todos los exámenes de licencia de taxi', de: 'Alle Tests für den Taxischein' },
  lockedF2: { en: 'Clear explanation for every question', sv: 'Tydlig förklaring till varje fråga', ar: 'شرح واضح لكل سؤال', tr: 'Her soru için net açıklama', fa: 'توضیح واضح برای هر سوال', so: 'Sharraxaad cad su\'aal kasta', pl: 'Jasne wyjaśnienie każdego pytania', es: 'Explicación clara de cada pregunta', de: 'Klare Erklärung zu jeder Frage' },
  lockedF3: { en: 'Track your progress until test day', sv: 'Följ din utveckling fram till provdagen', ar: 'تتبّع تقدمك حتى يوم الاختبار', tr: 'Sınav gününe kadar ilerlemeni takip et', fa: 'پیگیری پیشرفت تا روز آزمون', so: 'La soco horumarkaaga ilaa maalinta imtixaanka', pl: 'Śledź postępy aż do dnia egzaminu', es: 'Sigue tu progreso hasta el día del examen', de: 'Verfolge deinen Fortschritt bis zum Prüfungstag' },
  lockedViewPlans: { en: 'View plans', sv: 'Visa paket', ar: 'عرض الباقات', tr: 'Paketleri gör', fa: 'مشاهده بسته‌ها', so: 'Eeg qorshayaasha', pl: 'Zobacz pakiety', es: 'Ver planes', de: 'Pakete ansehen' },
  lockedLogin: { en: 'Already subscribed? Log in', sv: 'Har du redan ett abonnemang? Logga in', ar: 'لديك اشتراك بالفعل؟ تسجيل الدخول', tr: 'Zaten abone misin? Giriş yap', fa: 'قبلاً مشترک شده‌اید؟ ورود', so: 'Horey ma u isdiiwaangelisay? Gal', pl: 'Masz już subskrypcję? Zaloguj się', es: '¿Ya tienes suscripción? Inicia sesión', de: 'Schon abonniert? Anmelden' },
  authPromptTitle: { en: 'Create a Free Account', sv: 'Skapa ett gratis konto', ar: 'أنشئ حساباً مجانياً', tr: 'Ücretsiz Hesap Oluştur', fa: 'یک حساب رایگان بسازید', so: 'Samee Akoon Bilaash ah', pl: 'Załóż darmowe konto', es: 'Crea una cuenta gratis', de: 'Kostenloses Konto erstellen' },
  authPromptDesc: { en: 'Sign up to start your free questions and track your progress.', sv: 'Registrera dig för att börja med dina gratis frågor och följa din utveckling.', ar: 'سجّل لبدء أسئلتك المجانية ومتابعة تقدمك.', tr: 'ücretsiz sorunuza başlamak ve ilerlemenizi takip etmek için kaydolun.', fa: 'ثبت‌نام کنید تا سوال‌های رایگان خود را شروع کرده و پیشرفت‌تان را دنبال کنید.', so: 'Iska diiwaangeli si aad u bilowdo su\'aalo bilaash ah oo aad u raacdo horumarkaaga.', pl: 'Zarejestruj się, aby rozpocząć darmowe pytania i śledzić swoje postępy.', es: 'Regístrate para empezar tus preguntas gratis y seguir tu progreso.', de: 'Registriere dich, um deine kostenlosen Fragen zu starten und deinen Fortschritt zu verfolgen.' },
  authPromptSignUp: { en: 'Create Free Account', sv: 'Skapa gratis konto', ar: 'إنشاء حساب مجاني', tr: 'Ücretsiz Hesap Oluştur', fa: 'ایجاد حساب رایگان', so: 'Samee Akoon Bilaash', pl: 'Załóż darmowe konto', es: 'Crear cuenta gratis', de: 'Kostenloses Konto erstellen' },
  authPromptLogin: { en: 'I already have an account', sv: 'Jag har redan ett konto', ar: 'لدي حساب بالفعل', tr: 'Zaten hesabım var', fa: 'قبلاً حساب دارم', so: 'Akoon ayaan horay u lahaa', pl: 'Mam już konto', es: 'Ya tengo una cuenta', de: 'Ich habe bereits ein Konto' },
};

const TestsPage = () => {
  const { t, i18n } = useTranslation();
  const { token, user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [availableTests, setAvailableTests] = useState(() => {
    // Show cached tests immediately for everyone to eliminate the skeleton flash.
    // Backend re-validates lock state on every /api/tests/{id} request,
    // so stale `isLocked` flags can never grant unauthorized access.
    // The background fetch in useEffect refreshes lock state within ~300ms.
    const cached = getResolvedData('tests');
    return cached && Array.isArray(cached) && cached.length > 0 ? cached.map(test => ({
      id: test.id, name: test.title || '', title: test.title, description: test.description || '',
      questionsCount: test.questionCount || test.questionsCount || 30, duration: test.duration || test.timeLimit || 25,
      category: test.category, selectedQuestionIds: test.selectedQuestionIds || test.questionIds || [],
      isFreeTest: test.isFreeTest || false, isLocked: test.isLocked || false, requiresRegistration: test.requiresRegistration || false,
      isFullExam: test.isFullExam || false, isFinalExam: test.isFinalExam || false,
      isFixedQuestions: test.isFixedQuestions || false, order: test.order || parseInt(test.id?.replace(/\D/g, '') || '0') || 0
    })) : [];
  });
  // Selected license type (b_korkort default, or taxi).
  const [licenseType, setLicenseType] = useState('b_korkort');
  // For Taxi: which of the 3 sub-categories the user selected.
  // null | 'sakerhet_och_beteende' | 'lagstiftning' | 'kartlasning'
  const [taxiSubCategory, setTaxiSubCategory] = useState(null);
  // Whether the user has picked a license in THIS session. Always starts false
  // so the chooser is shown on every fresh visit — a cleaner, deliberate UX
  // instead of silently reusing a previous localStorage pick.
  const [licenseChosen, setLicenseChosen] = useState(false);
  // Feature flag: is the Taxi section publicly visible yet? Admins always see
  // it (to add questions). Regular users see it only when TAXI_ENABLED=true.
  // Starts as `null` so we DON'T auto-pick a license before the API answers
  // — otherwise regular users get flashed straight into B-körkort even when
  // Taxi is enabled.
  const [taxiPublic, setTaxiPublic] = useState(null);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const showTaxiCard = taxiPublic === true || isAdmin;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/features/public`, { timeout: 8000 });
        if (alive) setTaxiPublic(!!r?.data?.taxi_enabled);
      } catch (e) {
        if (alive) setTaxiPublic(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // If Taxi is disabled AND user is not admin, auto-pick B-körkort silently
  // so a regular visitor never sees the chooser at all (single-license UX).
  // WAIT for taxiPublic to be resolved (non-null) before deciding — otherwise
  // we auto-pick during the brief loading window even when Taxi is enabled.
  useEffect(() => {
    if (taxiPublic === null) return; // still loading feature flag
    if (!showTaxiCard && !licenseChosen) {
      setLicenseType('b_korkort');
      setLicenseChosen(true);
    }
  }, [taxiPublic, showTaxiCard, licenseChosen]);
  const [testsInitialized, setTestsInitialized] = useState(() => {
    // Initialize as ready whenever we have any cached data (guest or logged-in).
    // The background fetch in useEffect will refresh lock states immediately.
    const cached = getResolvedData('tests');
    return cached && Array.isArray(cached) && cached.length > 0;
  });
  const [selectedTest, setSelectedTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [wrongAnswers, setWrongAnswers] = useState({}); // Track wrong answers for immediate feedback
  const [testStarted, setTestStarted] = useState(false);
  const [showGuestResult, setShowGuestResult] = useState(false);
  const trialTokenRef = useRef(null);
  const finalSpec = licenseType === 'taxi' && taxiSubCategory === 'lagstiftning' ? { n: 50, p: 34 } : licenseType === 'taxi' ? { n: 70, p: 48 } : { n: 70, p: 52 };
  const location = useLocation();
  // After a guest signs up/logs in, reopen their last result with the full review.
  useEffect(() => {
    if (!token) return;
    try {
      const saved = JSON.parse(localStorage.getItem('guestPendingReview') || 'null');
      localStorage.removeItem('guestPendingReview');
      if (!saved || Date.now() - saved.ts > 24 * 3600 * 1000) return;
      setSelectedTest(saved.test);
      setQuestions(saved.questions);
      setResults(saved.results);
      setTestStarted(true);
      setTestSubmitted(true);
    } catch (e) { /* ignore bad data */ }
  }, [token]);
  // Navbar "Tests" link always returns to the licence chooser.
  useEffect(() => {
    if (!location.state?.resetTests) return;
    if (testStarted && !testSubmitted) return;
    if (testSubmitted) {
      setShowGuestResult(false);
      setShowNudge(false);
      setResults(null);
      setSelectedTest(null);
      setCurrentIndex(0);
      setAnswers({});
      setQuestions([]);
      setTestStarted(false);
      setTestSubmitted(false);
      setWrongAnswers({});
      setExpandedExplanations({});
      setTestTrackingId(null);
    }
    window.scrollTo({ top: 0 });
    if (!showTaxiCard) return;
    setLicenseChosen(false);
    setTaxiSubCategory(null);
    setLicenseType('b_korkort');
    window.scrollTo({ top: 0 });
  }, [location.key]);
  const [testSubmitted, setTestSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const [speechPhase, setSpeechPhase] = useState('idle');
  const speakingRef = useRef(null);
  useEffect(() => { speakingRef.current = speakingId; }, [speakingId]);
  const resultCardRef = useRef(null);

  // Stop any ongoing speech when navigating away / unmounting
  useEffect(() => () => stopSpeaking(), []);

  // Silence the voice as soon as the user moves to another question or submits.
  useEffect(() => {
    stopSpeaking();
    setSpeakingId(null);
  }, [currentIndex, testSubmitted, location.key]);

  const handleSpeak = useCallback((id, text) => {
    if (!text) return;
    if (speakingId === id) {
      stopSpeaking();
      setSpeakingId(null);
      return;
    }
    // Taxi tests are Swedish-only content — always read them in Swedish
    // regardless of the UI language the user picked.
    const speakLang = licenseType === 'taxi' ? 'sv' : i18n.language;
    speakingRef.current = id;
    setSpeakingId(id);
    setSpeechPhase('loading');
    speak(text, speakLang, {
      onStart: () => { if (speakingRef.current === id) setSpeechPhase('playing'); },
      onEnd: () => setSpeakingId((cur) => (cur === id ? null : cur)),
    });
  }, [speakingId, i18n.language, licenseType]);

  // Browser/system back-button (Android gesture, iOS swipe, desktop <-) should
  // step through the picker levels — chooser → sub-category → tests list —
  // instead of jumping straight to the previous route. We push ONE synthetic
  // history entry per drill-down (see pickLicense / pickTaxiCategory below)
  // and reverse the corresponding state on `popstate`. We do NOT push a new
  // entry from inside the handler — otherwise the user would need N+2 extra
  // back presses just to leave the page.
  useEffect(() => {
    const onPop = () => {
      // If the user is inside a running/completed test, don't hijack — let
      // the browser navigate away normally.
      if (testStarted || testSubmitted) return;
      // Deepest → shallow: taxi sub-category set → drop it to sub-chooser.
      if (licenseType === 'taxi' && taxiSubCategory) {
        setTaxiSubCategory(null);
        setLicenseChosen(false);
        return;
      }
      // From tests list or sub-chooser → main license chooser.
      if (licenseChosen || (licenseType === 'taxi' && !taxiSubCategory)) {
        setLicenseChosen(false);
        setTaxiSubCategory(null);
        setLicenseType('b_korkort');
        return;
      }
      // Already at the chooser — let the actual back navigation flow.
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [licenseChosen, licenseType, taxiSubCategory, testStarted, testSubmitted]);
  // Guards against race conditions when the user quickly switches between
  // license types or Taxi sub-categories: each new fetch bumps the generation
  // and any stale response with a lower generation is discarded.
  const fetchGenRef = useRef(0);

  // Google review prompt — shown after 3 completed tests with good score
  const reviewPrompt = useGoogleReviewPrompt({ apiBase: API });

  const captureResultCard = async () => {
    if (!resultCardRef.current) return null;
    const canvas = await html2canvas(resultCardRef.current, {
      backgroundColor: null,
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
  };

  const shareResultImage = async () => {
    const blob = await captureResultCard();
    if (!blob) return;
    const file = new File([blob], 'drivemaster-result.png', { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: getText(TEXTS.shareText).replace('{score}', results?.score_percentage || 0),
          text: getText(TEXTS.shareText).replace('{score}', results?.score_percentage || 0) + '\nhttps://drivemaster.se',
          files: [file],
        });
        return;
      } catch (e) { if (e.name === 'AbortError') return; }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'drivemaster-result.png'; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadResultImage = async () => {
    const blob = await captureResultCard();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'drivemaster-result.png'; a.click();
    URL.revokeObjectURL(url);
  };
  const [loading, setLoading] = useState(false);
  const [loadingTestId, setLoadingTestId] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(50 * 60); // 50 minutes in seconds
  const [showConfirmDialog, setShowConfirmDialog] = useState(false); // Confirmation dialog state
  const [showLeaveDialog, setShowLeaveDialog] = useState(false); // Leave test warning dialog
  const [expandedExplanations, setExpandedExplanations] = useState({});
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showAuthPromptModal, setShowAuthPromptModal] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [nextBusy, setNextBusy] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);
  useBodyScrollLock(showRegisterPrompt);
  const [checkingUserLimit, setCheckingUserLimit] = useState(true); // للتحكم في شاشة التحميل
  const [showReportModal, setShowReportModal] = useState(false); // مودال الإبلاغ عن خطأ
  const [zoomedImage, setZoomedImage] = useState(null);
  const [reportQuestion, setReportQuestion] = useState(null); // السؤال المراد الإبلاغ عنه
  const [reportType, setReportType] = useState(''); // نوع الخطأ
  const [reportDetails, setReportDetails] = useState(''); // تفاصيل الخطأ
  const [shuffledOptionsMap, setShuffledOptionsMap] = useState({}); // خريطة الخيارات المخلوطة
  const [languageKey, setLanguageKey] = useState(i18n.language); // مفتاح لإجبار إعادة العرض
  const [testTrackingId, setTestTrackingId] = useState(null);
  const testStartTimeRef = useRef(null);
  const isRTL = i18n.language === 'ar' || i18n.language === 'fa';

  // تحديث المفتاح عند تغيير اللغة لإجبار إعادة عرض الأسئلة
  useEffect(() => {
    setLanguageKey(i18n.language);
  }, [i18n.language]);

  // Add animation styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translateY(20px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      // Safe cleanup: only remove if still a child
      if (style.parentNode === document.head) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Pending navigation target
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Warn before closing browser/tab
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (testStarted && !testSubmitted) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [testStarted, testSubmitted]);

  // Override navigation - intercept clicks on links
  useEffect(() => {
    const handleClick = (e) => {
      if (!testStarted || testSubmitted) return;
      
      // Find if click was on a link or inside a link
      let target = e.target;
      while (target && target !== document) {
        if (target.tagName === 'A' && target.href) {
          const url = new URL(target.href);
          // Check if it's internal navigation (same origin and not the same page)
          if (url.origin === window.location.origin && url.pathname !== window.location.pathname) {
            e.preventDefault();
            e.stopPropagation();
            setPendingNavigation(url.pathname);
            setShowLeaveDialog(true);
            return;
          }
        }
        target = target.parentElement;
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [testStarted, testSubmitted]);

  // Timer countdown
  useEffect(() => {
    if (testStarted && !testSubmitted && timeRemaining > 0) {
      // Warning when 1 minute remaining
      if (timeRemaining === 60) {
        toast.warning(getText(TEXTS.oneMinuteRemaining), { duration: 5000 });
      }
      
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Show notification before auto-submit
            toast.error(getText(TEXTS.timeUp), { duration: 3000 });
            setTimeout(() => {
              submitTest(); // Auto-submit when time runs out
            }, 1000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [testStarted, testSubmitted, timeRemaining]);

  // Refs لمنع التكرار والوميض - يجب أن تكون في أعلى المكون
  const initialCheckDone = useRef(false);
  const autoStartAttempted = useRef(false);
  const [userChecked, setUserChecked] = useState(false);

  // Fetch available tests on mount and when subscription status changes
  // (so cards unlock immediately after a successful payment without manual refresh).
  // Language change is intentionally NOT a dependency — every test row already
  // contains all 9 language variants, and the UI picks the right one locally
  // via getTestTitle(). Refetching on language change just caused an unnecessary
  // skeleton flash for ~300ms.
  useEffect(() => {
    // Clear stale cached tests when license OR taxi sub-category switches so the
    // user never sees the wrong list flash while the refetch is in flight.
    setAvailableTests([]);
    setTestsInitialized(false);
    // Bump the fetch generation so any in-flight request for the previous
    // license/category is ignored when it eventually resolves (race guard).
    fetchGenRef.current += 1;
    fetchAvailableTests();
  }, [user?.subscription_status, user?.subscription_plan, licenseType, taxiSubCategory]);

  // Persist licenseType choice as a diagnostic/analytics hint. NOT used to
  // restore state — the chooser is shown fresh on every visit by design.
  useEffect(() => {
    try { localStorage.setItem('dm_license_type', licenseType); } catch (e) { /* localStorage unavailable */ }
  }, [licenseType]);

  // Re-fetch tests + refresh user when the tab becomes visible again
  // (e.g. user returns from Stripe checkout). This makes
  // subscribe/cancel/refund changes appear without a manual reload.
  //
  // IMPORTANT: deps must include licenseType + taxiSubCategory. Otherwise the
  // handler captures a stale `fetchAvailableTests` closure that still knows
  // about the previous license (e.g. b_korkort) and, on tab-switch, would
  // overwrite Taxi state with B-körkort tests. Always bump the fetch
  // generation before dispatching so any earlier in-flight fetch is discarded.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && token) {
        refreshUser?.();
        fetchGenRef.current += 1;
        fetchAvailableTests();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [token, licenseType, taxiSubCategory]);
  
  // Prefetch the first free test + the next few visible tests so clicking
  // a card feels instant on mobile (where hover-prefetch doesn't exist).
  // We stagger to avoid hammering the API.
  useEffect(() => {
    if (availableTests.length === 0) return;
    const toPrefetch = [];
    const free = availableTests.find(t => t.isFreeTest);
    if (free) toPrefetch.push(free.id);
    // Next 3 non-locked premium tests (subscribers) — for guests these are
    // locked so backend will reject prefetch with 403, which is fine.
    const visible = availableTests
      .filter(t => !t.isFreeTest && !t.isFinalExam && !t.isLocked)
      .slice(0, 3)
      .map(t => t.id);
    toPrefetch.push(...visible);
    toPrefetch.forEach((id, idx) => {
      // 250ms stagger between requests so the browser can keep rendering
      setTimeout(() => { try { prefetchDetail('test', id); } catch {} }, idx * 250);
    });
  }, [availableTests]);
  
  // التحقق من حد الأسئلة - مرة واحدة فقط عند تحميل الصفحة
  // تم تعديله: لا يظهر المودال تلقائياً، فقط يتحقق من الحالة
  useEffect(() => {
    // تجنب التكرار المطلق
    if (initialCheckDone.current || userChecked) return;
    if (!token) {
      setCheckingUserLimit(false);
      return;
    }
    
    // Don't wait for tests to load - just check user status
    const isTrialUser = (user?.subscription_status === 'trial' || user?.subscription_status === 'expired') && !user?.is_admin && user?.role !== 'super_admin';
    if (!isTrialUser) {
      setUserChecked(true);
      setCheckingUserLimit(false);
      return;
    }
    
    // تعليم فوري قبل أي عملية async
    initialCheckDone.current = true;
    setUserChecked(true);
    
    // فقط إنهاء التحميل - لا نظهر المودال تلقائياً
    // سيظهر المودال فقط عند محاولة بدء اختبار
    setCheckingUserLimit(false);
  }, [token, user, testStarted, userChecked]);

  const fetchAvailableTests = async (retryCount = 0) => {
    // Snapshot the generation at call time. Any state-setting later in this
    // async function is a no-op if a newer fetch has been kicked off.
    const gen = fetchGenRef.current;
    const isStale = () => gen !== fetchGenRef.current;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const currentLang = i18n.language || localStorage.getItem('i18nextLng') || 'sv';
      
      // For any LOGGED-IN user, always fetch fresh from API.
      // Reason: subscription state (active/trial/refunded/expired) can change
      // at any moment, and the prefetched list may carry stale isLocked flags
      // from a previous subscription state. Only anonymous guests may safely
      // re-use the prefetched response (their view never changes).
      const isLoggedIn = !!token;
      // Try prefetched data first (only on first attempt, only for guests,
      // and only for the DEFAULT license 'b_korkort' since the prefetch cache
      // is license-agnostic and contains B-körkort tests only).
      let data = (retryCount === 0 && !isLoggedIn && licenseType === 'b_korkort')
        ? await getPrefetched('tests')
        : null;
      
      if (!data) {
        // Cache-busting timestamp ensures we never get a stale CDN/proxy cache
        const ts = Date.now();
        const taxiCatParam = (licenseType === 'taxi' && taxiSubCategory)
          ? `&taxiCategory=${encodeURIComponent(taxiSubCategory)}`
          : '';
        const response = await axios.get(`${API}/tests/available?lang=${currentLang}&licenseType=${licenseType}${taxiCatParam}&_t=${ts}`, { headers, timeout: 15000 });
        data = response.data;
      }
      
      if (data && data.length > 0) {
        const getTestTitle = (test) => {
          const langMap = { ar: 'titleAr', en: 'titleEn', sv: 'title', tr: 'titleTr', fa: 'titleFa', so: 'titleSo', pl: 'titlePl', es: 'titleEs', de: 'titleDe' };
          return test[langMap[currentLang]] || test.title || test.titleEn || '';
        };
        
        const getTestDescription = (test) => {
          const langMap = { ar: 'descriptionAr', en: 'descriptionEn', sv: 'description', tr: 'descriptionTr', fa: 'descriptionFa', so: 'descriptionSo', pl: 'descriptionPl', es: 'descriptionEs', de: 'descriptionDe' };
          return test[langMap[currentLang]] || test.description || test.descriptionEn || '';
        };
        
        const tests = data.map(test => ({
          id: test.id,
          name: getTestTitle(test),
          title: test.title,
          description: getTestDescription(test),
          questionsCount: test.questionCount || test.questionsCount || 30,
          duration: test.duration || test.timeLimit || 25,
          category: test.category,
          selectedQuestionIds: test.selectedQuestionIds || test.questionIds || [],
          isFreeTest: test.isFreeTest || false,
          isLocked: test.isLocked || false,
          requiresRegistration: test.requiresRegistration || false,
          isFullExam: test.isFullExam || false,
          isFinalExam: test.isFinalExam || false,
          isFixedQuestions: test.isFixedQuestions || false,
          licenseType: test.licenseType || 'b_korkort',
          taxiCategory: test.taxiCategory || null,
          order: test.order || parseInt(test.id?.replace(/\D/g, '') || '0') || 0
        }));
        
        tests.sort((a, b) => {
          if (a.isFinalExam !== b.isFinalExam) return a.isFinalExam ? 1 : -1;
          const getNumber = (test) => {
            const nameMatch = test.name?.match(/(\d+)/);
            const idMatch = test.id?.match(/(\d+)/);
            return parseInt(nameMatch?.[1] || idMatch?.[1] || test.order || '999');
          };
          return getNumber(a) - getNumber(b);
        });
        
        if (isStale()) return;  // Discard stale response
        setAvailableTests(tests);
      } else if (retryCount < 2) {
        // Empty response - retry (but only if this fetch is still current)
        if (isStale()) return;
        await new Promise(r => setTimeout(r, 1000));
        return fetchAvailableTests(retryCount + 1);
      } else {
        // Genuinely empty (e.g., no tests exist for the selected licenseType).
        // Ensure the UI reflects this rather than keeping stale content.
        if (isStale()) return;
        setAvailableTests([]);
      }
    } catch (error) {
      console.error('Error fetching tests:', error);
      if (retryCount < 2 && !isStale()) {
        await new Promise(r => setTimeout(r, 1500));
        return fetchAvailableTests(retryCount + 1);
      }
    } finally {
      if (!isStale()) {
        setTestsInitialized(true);
        setLoading(false);
      }
    }
  };

  // دالة لخلط المصفوفة عشوائياً (Fisher-Yates shuffle)
  // Client-side option shuffling. Backend always sends DB-canonical order
  // with correctAnswer = canonical index. We shuffle on the client, store
  // the permutation, and submit the CANONICAL (DB) index so backend can
  // verify reliably regardless of how options were displayed.
  const processQuestionsWithShuffledOptions = (questionsData) => {
    const optionsMap = {};
    questionsData.forEach(q => {
      const questionId = q.id || q._id;
      const rawOptions = q.options || {};
      const baseArr = Array.isArray(rawOptions)
        ? rawOptions
        : (rawOptions.sv || rawOptions.en || Object.values(rawOptions)[0] || []);
      const optionsLength = baseArr.filter(o => o && o.trim && o.trim() !== '').length || 4;

      // Fisher-Yates shuffle of display indices 0..n-1
      const shuffledIndices = Array.from({ length: optionsLength }, (_, i) => i);
      for (let i = shuffledIndices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledIndices[i], shuffledIndices[j]] = [shuffledIndices[j], shuffledIndices[i]];
      }

      const canonicalCorrect = q.correct_answer ?? q.correctAnswer ?? 0;
      // newCorrectAnswer = position of canonical correct in the shuffled list
      const newCorrectAnswer = shuffledIndices.indexOf(canonicalCorrect);

      // Map from shuffled display index → original DB index
      // (i.e. shuffledIndices[displayIdx] = originalIdx)
      optionsMap[questionId] = {
        shuffledIndices,
        newCorrectAnswer,
        originalToNew: shuffledIndices.reduce((acc, originalIdx, displayIdx) => {
          acc[originalIdx] = displayIdx;
          return acc;
        }, {})
      };
    });
    setShuffledOptionsMap(optionsMap);
    return questionsData;
  };

  const fetchQuestions = async (testId) => {
    let currentTestId = testId;
    setLoadingTestId(testId);
    try {
      const test = availableTests.find(t => t.id === testId);
      if (test) {
        setSelectedTest(test);
        
        const isGuest = !user;
        const isTrialUser = user?.subscription_status === 'trial' || user?.subscription_status === 'expired';
        const isFreeTest = test.isFreeTest || false;
        const testQuestionCount = test.questionsCount || 30;
        
        const testDuration = test.duration || 25;
        setTimeRemaining(testDuration * 60);
        let questionsData = [];
        trialTokenRef.current = null;
        
        // Try prefetched test data first (from hover)
        const prefetchedTest = await getPrefetched(`test_${testId}`);
        
        if (isGuest || isTrialUser) {
          if (isFreeTest) {
            if (prefetchedTest?.questions) {
              questionsData = (prefetchedTest.questions || []).slice(0, testQuestionCount);
              trialTokenRef.current = prefetchedTest.trial_token || null;
            } else {
              try {
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const response = await axios.get(`${API}/tests/${testId}?_t=${Date.now()}`, { headers });
                questionsData = (response.data?.questions || []).slice(0, testQuestionCount);
              trialTokenRef.current = response.data?.trial_token || null;
              } catch (freeTestError) {
                console.log('Free test failed with token, trying without...');
                const response = await axios.get(`${API}/tests/${testId}?_t=${Date.now()}`);
                questionsData = (response.data?.questions || []).slice(0, testQuestionCount);
              trialTokenRef.current = response.data?.trial_token || null;
              }
            }
          } else {
            // الاختبارات المدفوعة: تُجلب من endpoint الاختبار المحدد ليطبّق
            // التقسيم المتناسب اليومي (لا تداخل بين البطاقات)
            const response = await axios.get(`${API}/tests/${testId}?_t=${Date.now()}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            questionsData = (response.data?.questions || []).slice(0, testQuestionCount);
              trialTokenRef.current = response.data?.trial_token || null;
          }
          // خلط ترتيب الأسئلة معطّل بناءً على طلب المستخدم - الترتيب يبقى ثابتاً دائماً
          processQuestionsWithShuffledOptions(questionsData);
        } else {
          if (isFreeTest) {
            // الاختبار المجاني: يبقى بنفس الأسئلة الثابتة
            if (prefetchedTest?.questions) {
              questionsData = (prefetchedTest.questions || []).slice(0, testQuestionCount);
              trialTokenRef.current = prefetchedTest.trial_token || null;
            } else {
              const response = await axios.get(`${API}/tests/${testId}?_t=${Date.now()}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              questionsData = (response.data?.questions || []).slice(0, testQuestionCount);
              trialTokenRef.current = response.data?.trial_token || null;
            }
          } else {
            // الاختبارات المدفوعة: تُجلب من endpoint الاختبار المحدد
            // (يطبّق التقسيم المتناسب لضمان 0% تداخل بين البطاقات)
            const response = await axios.get(`${API}/tests/${testId}?_t=${Date.now()}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            questionsData = (response.data?.questions || []).slice(0, testQuestionCount);
              trialTokenRef.current = response.data?.trial_token || null;
          }
          // خلط ترتيب الأسئلة معطّل بناءً على طلب المستخدم
          processQuestionsWithShuffledOptions(questionsData);
        }
        
        setQuestions(questionsData);
        
        if (refreshUser && token) {
          refreshUser().catch(() => {});
        }
        return questionsData.length > 0;
      }
      return false;
    } catch (error) {
      console.error('Error fetching questions:', error);
      const test = availableTests.find(t => t.id === currentTestId);
      // لا تعرض paywall للاختبار المجاني
      if (test?.isFreeTest) {
        toast.error(t('common.error'));
      } else if (error.response?.status === 403) {
        const lang = i18n?.language || 'sv';
        const msg = {
          sv: 'Prenumeration krävs för detta test',
          en: 'Subscription required for this test',
          ar: 'يجب الاشتراك للوصول إلى هذا الاختبار',
          tr: 'Bu test için abonelik gereklidir',
          fa: 'برای این آزمون اشتراک لازم است',
          so: 'Diiwaangelinta ayaa looga baahan yahay imtixaankan',
          pl: 'Subskrypcja wymagana do tego testu',
          es: 'Se requiere suscripción para este test',
          de: 'Abonnement erforderlich für diesen Test'
        };
        toast.error(msg[lang] || msg.sv);
        setShowSubscribeModal(true);
      } else {
        toast.error(t('common.error'));
      }
      return false;
    } finally {
      setLoadingTestId(null);
    }
  };

  const startTest = async (testId) => {
    // للمستخدمين التجريبيين: التحقق من استخدام الاختبار المجاني
    // للزوار: السماح فقط بالاختبارات المجانية
    const test = availableTests.find(t => t.id === testId);
    const isRegisterFree = test?.requiresRegistration || false;
    const isFreeTest = test?.isFreeTest || (isRegisterFree && !!user) || false;
    
    // Debug log
    console.log('Starting test:', { testId, isFreeTest, test, user: !!user });
    
    // الزوار: مسموح لهم بتجربة الاختبار المجاني مباشرة بدون تسجيل
    // عند محاولة فتح أي اختبار آخر، يظهر AuthPromptModal
    if (!user) {
      if (isRegisterFree) {
        setShowRegisterPrompt(true);
        return;
      }
      if (!isFreeTest) {
        console.log('Guest blocked - non-free test requires auth');
        trackPaywallShown('/tests');
        setShowAuthPromptModal(true);
        return;
      }
      // free test: continue without auth — guest can take questions for free
    }
    
    // المستخدمون التجريبيون الذين استخدموا الاختبار المجاني أو المنتهية اشتراكاتهم
    if ((user?.subscription_status === 'expired' && !isFreeTest) ||
        (user?.subscription_status === 'trial' && user?.has_used_free_test && !isFreeTest)) {
      trackPaywallShown('/tests');
      setShowAuthPromptModal(true);
      return;
    }
    
    const success = await fetchQuestions(testId);
    
    // Don't start test if no questions loaded
    if (!success) {
      return;
    }
    
    setTestStarted(true);
    setAnswers({});
    setWrongAnswers({});
    setCurrentIndex(0);
    setTestSubmitted(false);
    setExpandedExplanations({});
    testStartTimeRef.current = Date.now();

    // تتبع بدء الاختبار - غير حاجب (fire-and-forget)
    axios.post(`${API}/analytics/test-start`, {
      test_id: testId,
      test_name: test?.name || testId,
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      user_id: user?.id || null,
      total_questions: test?.questionsCount || 30,
      language: i18n.language,
    }).then(res => {
      if (res.data?.session_id) setTestTrackingId(res.data.session_id);
    }).catch(() => {});
  };

  const toggleExplanation = (questionId) => {
    setExpandedExplanations(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const selectAnswer = (questionId, answerIndex) => {
    // منع تغيير الإجابة بعد الاختيار الأول
    // Prevent changing answer after first selection
    if (answers[questionId] !== undefined) {
      return; // Already answered - ignore click
    }
    
    const question = questions.find(q => q.id === questionId);
    const shuffleData = shuffledOptionsMap[questionId];
    
    // استخدام الإجابة الصحيحة المحدثة بعد الخلط
    const correctAnswerIndex = shuffleData 
      ? shuffleData.newCorrectAnswer 
      : (question.correctAnswer ?? question.correct_answer ?? 0);
    
    const isCorrect = answerIndex === correctAnswerIndex;
    
    // حفظ الإجابة بالفهرس المعروض (للعرض في الـ UI)
    setAnswers({ ...answers, [questionId]: answerIndex });
    
    // Track wrong answers for visual feedback only (no toast messages)
    if (!isCorrect) {
      setWrongAnswers({ ...wrongAnswers, [questionId]: answerIndex });
    }

    // تتبع التقدم
    if (testTrackingId) {
      const newCount = Object.keys(answers).length + 1;
      try {
        navigator.sendBeacon?.(`${API}/analytics/test-progress`, 
          new Blob([JSON.stringify({ test_session_id: testTrackingId, answers_count: newCount, current_question: currentIndex + 1 })], {type: 'application/json'})
        );
      } catch { /* silent */ }
    }
  };
  
  // Convert user's display-index choices back to canonical DB indices.
  // The backend stores correctAnswer as the canonical DB position, so we
  // must send the canonical index (not the shuffled display position).
  const getOriginalAnswers = () => {
    const out = {};
    Object.entries(answers).forEach(([questionId, selectedDisplayIdx]) => {
      const map = shuffledOptionsMap[questionId];
      if (map && Array.isArray(map.shuffledIndices)) {
        // shuffledIndices[displayIdx] = canonical/original idx
        out[questionId] = map.shuffledIndices[selectedDisplayIdx];
      } else {
        out[questionId] = selectedDisplayIdx;
      }
    });
    return out;
  };

  // Show confirmation dialog before finishing test
  const handleFinishClick = () => {
    setShowConfirmDialog(true);
  };

  // Confirmed - proceed with test submission
  const confirmFinishTest = () => {
    setShowConfirmDialog(false);
    submitTest();
  };

  const submitTest = async () => {
    // Allow submission even if not all questions are answered
    // Unanswered questions will be counted as wrong
    const unansweredCount = questions.length - Object.keys(answers).length;
    
    if (unansweredCount > 0) {
      // Show warning but continue with submission
      const warningMsg = {
        ar: `⚠️ لم تجب على ${unansweredCount} سؤال. سيتم احتسابها كإجابات خاطئة.`,
        sv: `⚠️ Du har inte svarat på ${unansweredCount} frågor. De räknas som fel.`,
        en: `⚠️ You haven't answered ${unansweredCount} questions. They will be counted as wrong.`,
        tr: `⚠️ ${unansweredCount} soruyu cevaplamadınız. Yanlış olarak sayılacaklar.`,
        fa: `⚠️ شما به ${unansweredCount} سوال پاسخ نداده‌اید. آنها به عنوان اشتباه محاسبه می‌شوند.`,
        so: `⚠️ Ma jawaabin ${unansweredCount} su'aalood. Waxaa loo tirin doonaa qalad.`,
        pl: `⚠️ Nie odpowiedziałeś na ${unansweredCount} pytań. Zostaną policzone jako błędne.`,
        es: `⚠️ No has respondido ${unansweredCount} preguntas. Se contarán como incorrectas.`,
        de: `⚠️ Sie haben ${unansweredCount} Fragen nicht beantwortet. Sie werden als falsch gewertet.`
      };
      toast.warning(warningMsg[i18n.language] || warningMsg.en, { duration: 3000 });
    }

    setLoading(true);
    try {
      // تحويل الإجابات للفهارس الأصلية قبل الإرسال
      const originalAnswers = getOriginalAnswers();
      // إرسال بدون token إذا كان الزائر غير مسجل
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/tests/submit`, { answers: originalAnswers, total_questions: questions.length, test_id: selectedTest?.id || null, trial_token: trialTokenRef.current }, { headers });
      setResults(response.data);
      setTestSubmitted(true);
      // Guests unlock the answer review after signing up — keep it for that moment.
      if (!token) {
        try {
          localStorage.setItem('guestPendingReview', JSON.stringify({ test: selectedTest, questions, results: response.data, ts: Date.now() }));
        } catch (e) { /* storage full */ }
        setShowGuestResult(true);
      } else if (selectedTest?.requiresRegistration) {
        const isTaxiTest = selectedTest?.licenseType === 'taxi';
        const hasSub = isTaxiTest ? user?.taxi_subscription_status === 'active' : (user?.subscription_status === 'active' || user?.subscription_plan === 'lifetime');
        if (!hasSub && !user?.is_admin && user?.role !== 'super_admin') setTimeout(() => setShowNudge(true), 1200);
      }

      // تتبع إكمال الاختبار
      if (testTrackingId) {
        const timeSpent = testStartTimeRef.current ? Math.round((Date.now() - testStartTimeRef.current) / 1000) : null;
        try {
          axios.post(`${API}/analytics/test-complete`, {
            test_session_id: testTrackingId,
            status: 'completed',
            answers_count: Object.keys(answers).length,
            score: response.data?.correct_answers,
            score_percentage: response.data?.score_percentage,
            time_spent_seconds: timeSpent,
          });
        } catch { /* silent */ }
      }

      // Trigger Google review prompt after a test is completed
      try {
        reviewPrompt.triggerAfterTest(response.data?.score_percentage);
      } catch { /* silent */ }
      
      // تحديث بيانات المستخدم بعد إرسال الاختبار - فقط للمستخدمين المسجلين
      if (refreshUser && token) {
        await refreshUser();
      }
    } catch (error) {
      if (error.response?.status === 403) {
        // User exceeded question limit
        toast.error(error.response?.data?.detail || 'Question limit exceeded');
        setShowSubscribeModal(true);
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // ترجمات مودال الإبلاغ
  const REPORT_TEXTS = {
    selectError: { sv: 'Välj feltyp', ar: 'الرجاء اختيار نوع الخطأ', en: 'Please select error type', tr: 'Lütfen hata türünü seçin', fa: 'لطفا نوع خطا را انتخاب کنید', so: 'Fadlan dooro nooca khaladka', pl: 'Wybierz typ błędu', es: 'Selecciona el tipo de error', de: 'Bitte Fehlertyp auswählen' },
    reportSuccess: { sv: 'Rapport skickad! Tack för din hjälp', ar: 'تم إرسال البلاغ بنجاح! شكراً لمساعدتنا', en: 'Report submitted successfully! Thank you', tr: 'Rapor gönderildi! Teşekkürler', fa: 'گزارش با موفقیت ارسال شد! متشکریم', so: 'Warbixinta la diray! Mahadsanid', pl: 'Zgłoszenie wysłane! Dziękujemy', es: '¡Reporte enviado! Gracias', de: 'Bericht gesendet! Danke' },
    reportError: { sv: 'Fel vid skickande av rapport', ar: 'حدث خطأ أثناء إرسال البلاغ', en: 'Error submitting report', tr: 'Rapor gönderilirken hata', fa: 'خطا در ارسال گزارش', so: 'Khalad markii la dirayay', pl: 'Błąd wysyłania zgłoszenia', es: 'Error al enviar', de: 'Fehler beim Senden' },
    reportTitle: { sv: 'Rapportera fel', ar: 'الإبلاغ عن خطأ', en: 'Report Error', tr: 'Hata Bildir', fa: 'گزارش خطا', so: 'Soo sheeg khalad', pl: 'Zgłoś błąd', es: 'Reportar error', de: 'Fehler melden' },
    helpUs: { sv: 'Hjälp oss förbättra frågorna', ar: 'ساعدنا في تحسين الأسئلة بالإبلاغ عن أي خطأ', en: 'Help us improve by reporting errors', tr: 'Hataları bildirerek geliştirmemize yardımcı olun', fa: 'با گزارش خطاها به ما کمک کنید', so: 'Naga caawi inaad soo sheegto khaladaadka', pl: 'Pomóż nam zgłaszając błędy', es: 'Ayúdanos reportando errores', de: 'Helfen Sie uns durch Fehlermeldungen' },
    errorType: { sv: 'Feltyp', ar: 'نوع الخطأ', en: 'Error Type', tr: 'Hata Türü', fa: 'نوع خطا', so: 'Nooca khaladka', pl: 'Typ błędu', es: 'Tipo de error', de: 'Fehlertyp' },
    additionalDetails: { sv: 'Ytterligare detaljer (valfritt)', ar: 'تفاصيل إضافية (اختياري)', en: 'Additional Details (optional)', tr: 'Ek detaylar (isteğe bağlı)', fa: 'جزئیات اضافی (اختیاری)', so: 'Faahfaahin dheeraad ah (ikhtiyaar)', pl: 'Dodatkowe szczegóły (opcjonalnie)', es: 'Detalles adicionales (opcional)', de: 'Zusätzliche Details (optional)' },
    describeError: { sv: 'Beskriv felet här...', ar: 'اكتب تفاصيل الخطأ هنا...', en: 'Describe the error here...', tr: 'Hatayı burada açıklayın...', fa: 'خطا را اینجا شرح دهید...', so: 'Sharax khaladka halkan...', pl: 'Opisz błąd tutaj...', es: 'Describe el error aquí...', de: 'Beschreiben Sie den Fehler hier...' },
    submitReport: { sv: 'Skicka rapport', ar: 'إرسال البلاغ', en: 'Submit Report', tr: 'Raporu Gönder', fa: 'ارسال گزارش', so: 'Dir warbixinta', pl: 'Wyślij zgłoszenie', es: 'Enviar reporte', de: 'Bericht senden' },
    cancel: { sv: 'Avbryt', ar: 'إلغاء', en: 'Cancel', tr: 'İptal', fa: 'لغو', so: 'Jooji', pl: 'Anuluj', es: 'Cancelar', de: 'Abbrechen' }
  };

  const getReportText = (textObj) => textObj[i18n.language] || textObj.en;

  // دالة إرسال الإبلاغ عن خطأ
  const handleReportSubmit = async () => {
    if (!reportType) {
      toast.error(getReportText(REPORT_TEXTS.selectError));
      return;
    }
    
    try {
      await axios.post(`${API}/question-reports`, {
        question_id: reportQuestion?.id,
        report_type: reportType,
        details: reportDetails,
        question_text: reportQuestion?.question?.[i18n.language] || reportQuestion?.question?.sv
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(getReportText(REPORT_TEXTS.reportSuccess));
      setShowReportModal(false);
      setReportQuestion(null);
      setReportType('');
      setReportDetails('');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(getReportText(REPORT_TEXTS.reportError));
    }
  };

  // أنواع الأخطاء للإبلاغ - 9 لغات
  const reportTypes = [
    { value: 'wrong_answer', label: { ar: 'الإجابة الصحيحة خاطئة', sv: 'Fel svar markerat som rätt', en: 'Wrong answer marked as correct', tr: 'Yanlış cevap doğru olarak işaretlenmiş', fa: 'پاسخ اشتباه به عنوان صحیح علامت زده شده', so: 'Jawaab khaldan oo loo calaamadeeyay sax', pl: 'Błędna odpowiedź oznaczona jako poprawna', es: 'Respuesta incorrecta marcada como correcta', de: 'Falsche Antwort als richtig markiert' } },
    { value: 'translation_error', label: { ar: 'خطأ في الترجمة', sv: 'Översättningsfel', en: 'Translation error', tr: 'Çeviri hatası', fa: 'خطای ترجمه', so: 'Khalad turjumaad', pl: 'Błąd tłumaczenia', es: 'Error de traducción', de: 'Übersetzungsfehler' } },
    { value: 'missing_image', label: { ar: 'صورة مفقودة', sv: 'Bild saknas', en: 'Missing image', tr: 'Eksik resim', fa: 'تصویر گمشده', so: 'Sawir maqan', pl: 'Brakujący obraz', es: 'Imagen faltante', de: 'Fehlendes Bild' } },
    { value: 'unclear_question', label: { ar: 'السؤال غير واضح', sv: 'Otydlig fråga', en: 'Unclear question', tr: 'Belirsiz soru', fa: 'سوال نامشخص', so: 'Su\'aal aan caddayn', pl: 'Niejasne pytanie', es: 'Pregunta poco clara', de: 'Unklare Frage' } },
    { value: 'other', label: { ar: 'خطأ آخر', sv: 'Annat fel', en: 'Other error', tr: 'Diğer hata', fa: 'خطای دیگر', so: 'Khalad kale', pl: 'Inny błąd', es: 'Otro error', de: 'Anderer Fehler' } }
  ];

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 ? ((currentIndex + 1) / questions.length) * 100 : 0;

  // Preload next 3 question images for instant display
  useEffect(() => {
    if (!questions || questions.length === 0) return;
    const preloadCount = 3;
    for (let i = 1; i <= preloadCount; i++) {
      const nextQ = questions[currentIndex + i];
      if (nextQ && (nextQ.image_url || nextQ.image)) {
        const imgSrc = nextQ.image_url || nextQ.image;
        if (imgSrc && imgSrc !== 'None' && imgSrc !== '') {
          const img = new Image();
          img.src = imgSrc.startsWith('/api/uploads/') ? imgSrc : (imgSrc.startsWith('http') ? imgSrc : imgSrc);
        }
      }
    }
  }, [currentIndex, questions]);

  // Auto-scroll on question change so navigation buttons (Prev/Next/Submit) stay visible
  const scrollToQuestion = useCallback(() => {
    // Use scrollIntoView on the navigation buttons - guaranteed to work regardless of image loading
    const doScroll = () => {
      const navBtn = document.querySelector('[data-testid="next-btn"], [data-testid="submit-btn"]');
      if (navBtn) {
        navBtn.scrollIntoView({ block: 'end', behavior: 'auto' });
      } else {
        // Fallback: scroll to top of question card
        const card = document.querySelector('[data-testid="question-card"]');
        if (card) card.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    };
    // Run after DOM settles & after image loads
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        doScroll();
        // Run again after images likely loaded to fix any layout shift
        setTimeout(doScroll, 250);
      });
    });
  }, []);

  useEffect(() => {
    if (!questions || questions.length === 0) return;
    if (testSubmitted) return;
    scrollToQuestion();
  }, [currentIndex, testSubmitted, questions, scrollToQuestion]);

  // Auto-scroll the user toward the post-test subscribe CTA shortly after
  // the result screen renders, so mobile users don't miss the offer hiding
  // below the fold. Trigger when results become available for a non-paying
  // user on a free test.
  const _ctaCandidate =
    testSubmitted &&
    results &&
    (selectedTest?.isFreeTest ||
      !user ||
      user?.subscription_status === 'trial' ||
      user?.subscription_status === 'expired') &&
    !user?.is_admin &&
    user?.role !== 'super_admin';
  useEffect(() => {
    if (!_ctaCandidate) return;
    const timer = setTimeout(() => {
      const el = document.querySelector('[data-testid="subscribe-cta"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 2200);
    return () => clearTimeout(timer);
  }, [_ctaCandidate]);

  // Subscribe Modal Component
  const SubscribeModal = () => {
    if (!showSubscribeModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full p-8 relative shadow-2xl transform animate-in fade-in zoom-in duration-300">
          <button
            onClick={() => setShowSubscribeModal(false)}
            className="absolute top-4 end-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
          
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            
            <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {getText(TEXTS.freeQuestionsFinished)}
            </h3>
            
            <p className="text-slate-600 dark:text-slate-400 text-base">
              {getText(TEXTS.freeQuestionsUsed)}
            </p>
          </div>
          
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/priser')}
              className="w-full h-14 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all"
            >
              {getText(TEXTS.subscribeNow)}
            </Button>
            
            <Button
              onClick={() => setShowSubscribeModal(false)}
              variant="outline"
              className="w-full h-12 rounded-xl border-2 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 font-semibold"
            >
              {getText(TEXTS.cancel)}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const RegisterPromptModal = () => {
    if (!showRegisterPrompt) return null;
    const close = () => setShowRegisterPrompt(false);
    const go = (path) => { close(); goAuth(navigate, path, i18n.language, location.pathname); };
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="register-prompt-modal" onClick={close}>
        <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full p-6 sm:p-8 relative shadow-2xl animate-in fade-in zoom-in duration-300" onClick={(e) => e.stopPropagation()}>
          <button onClick={close} className="absolute top-4 end-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors" data-testid="register-prompt-close" aria-label="close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-sky-400 to-blue-600 rounded-full mb-4 shadow-lg shadow-sky-500/30">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2" data-testid="register-prompt-title">{getText(TEXTS.regTitle)}</h3>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">{getText(TEXTS.regDesc)}</p>
          </div>
          <div className="space-y-3">
            <Button onClick={() => go('/registrera')} data-testid="register-prompt-create-btn" className="w-full h-14 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-bold text-lg shadow-lg">
              {getText(TEXTS.regCreate)}
            </Button>
            <button onClick={() => go('/logga-in')} data-testid="register-prompt-login-btn" className="w-full pt-1 text-sm font-semibold text-slate-500 hover:text-sky-600 transition-colors">
              {getText(TEXTS.regLogin)}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Locked-content modal — explains a subscription is needed (guests and non-subscribed users)
  const AuthPromptModal = () => (
    <UnlockPlansModal
      open={showAuthPromptModal}
      onClose={() => setShowAuthPromptModal(false)}
      license={licenseType === 'taxi' ? 'taxi' : 'b'}
      lang={i18n.language}
    />
  );

  // مودال الإبلاغ عن خطأ
  const ReportModal = () => {
    if (!showReportModal) return null;
    
    const modalRTL = ['ar', 'fa'].includes(i18n.language);
    
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-3xl max-w-md w-full p-6 relative shadow-2xl" dir={modalRTL ? 'rtl' : 'ltr'}>
          <button
            onClick={() => {
              setShowReportModal(false);
              setReportQuestion(null);
              setReportType('');
              setReportDetails('');
            }}
            className="absolute top-4 end-4 p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
          
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full mb-4">
              <Flag className="w-7 h-7 text-white" />
            </div>
            
            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">
              {getReportText(REPORT_TEXTS.reportTitle)}
            </h3>
            
            <p className="text-slate-600 dark:text-slate-400 text-sm">
              {getReportText(REPORT_TEXTS.helpUs)}
            </p>
          </div>
          
          {/* نوع الخطأ */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              {getReportText(REPORT_TEXTS.errorType)}
            </label>
            <div className="space-y-2">
              {reportTypes.map((type) => (
                <label
                  key={type.value}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                    reportType === type.value 
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30' 
                      : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="reportType"
                    value={type.value}
                    checked={reportType === type.value}
                    onChange={(e) => setReportType(e.target.value)}
                    className="w-4 h-4 text-orange-500"
                  />
                  <span className="text-sm text-slate-800 dark:text-slate-200">{type.label[i18n.language] || type.label.en}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* تفاصيل إضافية */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              {getReportText(REPORT_TEXTS.additionalDetails)}
            </label>
            <textarea
              value={reportDetails}
              onChange={(e) => setReportDetails(e.target.value)}
              placeholder={getReportText(REPORT_TEXTS.describeError)}
              className="w-full p-3 border-2 border-slate-200 dark:border-slate-600 rounded-xl resize-none h-24 focus:border-orange-500 focus:outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            />
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={handleReportSubmit}
              className="flex-1 h-12 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold"
            >
              {getReportText(REPORT_TEXTS.submitReport)}
            </Button>
            <Button
              onClick={() => {
                setShowReportModal(false);
                setReportQuestion(null);
                setReportType('');
                setReportDetails('');
              }}
              variant="outline"
              className="flex-1 h-12 rounded-xl border-2 dark:border-slate-600"
            >
              {getReportText(REPORT_TEXTS.cancel)}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // عرض شاشة تحميل للمستخدمين التجريبيين حتى يتم الفحص
  const isTrialUserCheck = (user?.subscription_status === 'trial' || user?.subscription_status === 'expired') && !user?.is_admin && user?.role !== 'super_admin';
  if (isTrialUserCheck && checkingUserLimit && !testStarted) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50" data-testid="loading-check">
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-600">
              {getText(TEXTS.checking)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Start screen
  if (!testStarted) {
    // Full-screen license chooser — shown on very first visit (or after user
    // presses "Change license"). Two big beautiful cards, then user enters.
    if (!licenseChosen) {
      const pickLicense = (lic) => {
        setLicenseType(lic);
        // Push a synthetic history entry so the browser back-button and
        // Android system back trigger our popstate handler, which walks the
        // picker back one step instead of jumping to the previous route.
        try { window.history.pushState({ dm_pick: `license:${lic}` }, ''); } catch (_) { /* history unavailable */ }
        // For B-körkort just enter tests. For Taxi show the 3-category chooser.
        if (lic === 'b_korkort') {
          setLicenseChosen(true);
        } else {
          setLicenseType('taxi');
          // taxiSubCategory stays null so the sub-chooser renders below
        }
      };
      const pickTaxiCategory = (cat) => {
        setTaxiSubCategory(cat);
        setLicenseChosen(true);
        try { window.history.pushState({ dm_pick: `taxi-cat:${cat}` }, ''); } catch (_) { /* history unavailable */ }
      };
      // If user already picked Taxi but not a sub-category yet — show 3 sub-cards.
      if (licenseType === 'taxi' && !taxiSubCategory) {
        const isAr = i18n.language === 'ar';
        const categories = [
          {
            key: 'sakerhet_och_beteende',
            title: 'Säkerhet och beteende',
            subtitle: isAr ? 'الأمان والسلوك' : 'Safety & behaviour',
            emoji: '🛡️',
            gradient: 'from-rose-500 via-red-500 to-rose-600',
            shadow: 'shadow-rose-500/40 hover:shadow-rose-500/60',
          },
          {
            key: 'lagstiftning',
            title: 'Lagstiftning',
            subtitle: isAr ? 'التشريعات والقوانين' : 'Legislation',
            emoji: '⚖️',
            gradient: 'from-indigo-500 via-blue-500 to-indigo-600',
            shadow: 'shadow-indigo-500/40 hover:shadow-indigo-500/60',
          },
        ];
        return (
          <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950" data-testid="taxi-category-chooser">
            <SEO page="tests" />
            <Navbar />
            <main className="flex-grow pt-20 sm:pt-24 pb-16">
              <div className="max-w-6xl mx-auto px-4 sm:px-6">
                {/* Subtle back link — returns to the license chooser (B/Taxi) */}
                <button
                  type="button"
                  onClick={() => { setLicenseType('b_korkort'); setTaxiSubCategory(null); setLicenseChosen(false); }}
                  data-testid="taxi-back-btn"
                  className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                >
                  <span aria-hidden>←</span>
                  {isAr ? 'رجوع' : 'Tillbaka'}
                </button>

                {/* Centered header block */}
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-xs font-bold">
                    🚕 Taxikort
                  </div>
                  <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-slate-100 mb-2">
                    Taxikortprovet
                  </h1>
                </div>

                {/* Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {categories.map((cat, i) => (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => pickTaxiCategory(cat.key)}
                      data-testid={`taxi-category-${cat.key}`}
                      className={`group relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br ${cat.gradient} p-5 sm:p-8 text-white shadow-xl ${cat.shadow} transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] text-left rtl:text-right`}
                    >
                      <div className="absolute inset-0 bg-grid-white/[0.06] bg-[size:24px_24px]" />
                      <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/10 blur-3xl group-hover:bg-white/20 transition-all" />
                      <div className="absolute top-3 end-3 px-2 py-0.5 rounded-md bg-white/15 backdrop-blur-sm text-[10px] font-bold tracking-wider text-white/90">
                        {isAr ? `القسم ${i + 1}` : `Del ${i + 1}`}
                      </div>
                      <div className="relative z-10">
                        <div className="text-4xl sm:text-6xl mb-3">{cat.emoji}</div>
                        <div className="text-[10px] sm:text-xs font-bold tracking-wider uppercase text-white/80 mb-1">
                          {cat.subtitle}
                        </div>
                        <h2 className="text-xl sm:text-2xl font-black mb-4 leading-tight">
                          {cat.title}
                        </h2>
                        <span className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold border border-white/30 group-hover:bg-white/30 transition-colors">
                          {isAr ? 'ابدأ' : 'Kom igång'}
                          <span aria-hidden>→</span>
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </main>
            <Footer />
          </div>
        );
      }
      return (
        <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950" data-testid="license-chooser-page">
          <SEO page="tests" />
          <Navbar />
          <main className="flex-grow pt-24 pb-16">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 sm:mb-12">
                <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-slate-100 mb-3">
                  {i18n.language === 'ar' ? 'اختر نوع الشهادة' : 'Välj körkortstyp'}
                </h1>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                  {i18n.language === 'ar'
                    ? 'اختر النوع الذي تريد التدرّب عليه. يمكنك التبديل في أي وقت.'
                    : 'Vilken licens vill du öva för? Du kan byta när som helst.'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-8 items-stretch">
                {/* B-körkort card */}
                <button
                  type="button"
                  onClick={() => pickLicense('b_korkort')}
                  data-testid="license-card-b-korkort"
                  className="group relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-emerald-500 via-teal-500 to-emerald-600 p-4 sm:p-10 text-white shadow-xl sm:shadow-2xl shadow-emerald-500/40 hover:shadow-emerald-500/60 transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] text-left rtl:text-right h-full flex"
                >
                  <div className="absolute inset-0 bg-grid-white/[0.06] bg-[size:24px_24px]" />
                  <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-white/10 blur-3xl group-hover:bg-white/20 transition-all" />
                  <div className="relative z-10 flex flex-col w-full">
                    <div className="text-4xl sm:text-7xl mb-2 sm:mb-4">🚗</div>
                    <div className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase text-emerald-100 mb-1 sm:mb-2">
                      {i18n.language === 'ar' ? 'قيادة عادية' : 'Personbil'}
                    </div>
                    <h2 className="text-lg sm:text-4xl font-black mb-2 sm:mb-3 leading-tight">B-körkort</h2>
                    {/* Spacer to match Taxi card's Swedish-only badge — keeps
                        both cards visually aligned even in RTL mobile. */}
                    <div className="mb-2 sm:mb-3 invisible pointer-events-none max-w-full overflow-hidden inline-flex items-center gap-1 sm:gap-1.5 rounded-full px-2.5 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold">
                      <span>🇸🇪</span><span>Endast</span>
                    </div>
                    <span className="inline-flex items-center gap-1 sm:gap-2 self-start bg-white/20 backdrop-blur-sm px-2.5 sm:px-4 py-1 sm:py-2 rounded-full text-[11px] sm:text-sm font-bold border border-white/30 group-hover:bg-white/30 transition-colors mt-auto">
                      {i18n.language === 'ar' ? 'ابدأ' : 'Kom igång'}
                      <span aria-hidden>→</span>
                    </span>
                  </div>
                </button>

                {/* Taxi card */}
                <button
                  type="button"
                  onClick={() => pickLicense('taxi')}
                  data-testid="license-card-taxi"
                  className="group relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 p-4 sm:p-10 text-white shadow-xl sm:shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] text-left rtl:text-right h-full flex"
                >
                  <div className="absolute inset-0 bg-grid-white/[0.06] bg-[size:24px_24px]" />
                  <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-white/10 blur-3xl group-hover:bg-white/20 transition-all" />
                  <div className="relative z-10 flex flex-col w-full">
                    <div className="text-4xl sm:text-7xl mb-2 sm:mb-4">🚕</div>
                    <div className="text-[10px] sm:text-xs font-bold tracking-wider sm:tracking-widest uppercase text-amber-100 mb-1 sm:mb-2">
                      {i18n.language === 'ar' ? 'سائق تاكسي' : 'Yrkestrafik'}
                    </div>
                    <h2 className="text-lg sm:text-4xl font-black mb-2 sm:mb-3 leading-tight">Taxikort</h2>
                    {/* Notice: Taxi content only exists in Swedish today. Made
                        prominent so multilingual users know before diving in. */}
                    <div className="mb-2 sm:mb-3 self-start inline-flex items-center gap-1 sm:gap-1.5 bg-white/25 backdrop-blur-sm border border-white/40 rounded-full px-2.5 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold max-w-full leading-tight">
                      <span>🇸🇪</span>
                      <span>
                        {i18n.language === 'ar' ? 'بالسويدية فقط'
                          : i18n.language === 'sv' ? 'Endast på svenska'
                          : i18n.language === 'en' ? 'Swedish only'
                          : i18n.language === 'tr' ? 'İsveççe'
                          : i18n.language === 'fa' ? 'فقط سوئدی'
                          : i18n.language === 'so' ? 'Iswiidhish'
                          : i18n.language === 'pl' ? 'Po szwedzku'
                          : i18n.language === 'es' ? 'Solo en sueco'
                          : i18n.language === 'de' ? 'Nur Schwedisch'
                          : 'Endast på svenska'}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-1 sm:gap-2 self-start bg-white/20 backdrop-blur-sm px-2.5 sm:px-4 py-1 sm:py-2 rounded-full text-[11px] sm:text-sm font-bold border border-white/30 group-hover:bg-white/30 transition-colors mt-auto">
                      {i18n.language === 'ar' ? 'ابدأ' : 'Kom igång'}
                      <span aria-hidden>→</span>
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      );
    }
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950" data-testid="tests-page">
        <SEO page="tests" />
        <Navbar />
        <VisibleBreadcrumbs />
        <main className="flex-grow pt-3 pb-16">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            {/* Back Button — steps back through the picker flow instead of
                jumping to home in one shot (B/Taxi chooser is our own screen). */}
            <div className="mb-3">
              <BackButton
                onClick={() => {
                  // From tests list, go back one screen:
                  //  - Taxi + sub-category → back to sub-category chooser
                  //  - anything else with a license picked → license chooser
                  //  - already on license chooser → home
                  if (licenseType === 'taxi' && taxiSubCategory) {
                    setTaxiSubCategory(null);
                    setLicenseChosen(false);
                    return;
                  }
                  if (licenseChosen) {
                    setLicenseChosen(false);
                    setTaxiSubCategory(null);
                    setLicenseType('b_korkort');
                    return;
                  }
                  navigate('/');
                }}
              />
            </div>
            
            {/* Hero Section - Connected to Info Bar */}
            <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-t-2xl sm:rounded-t-3xl px-3 py-3 sm:p-6 text-white text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
              <div className="relative z-10">
                <h1 className="text-lg sm:text-3xl font-bold mb-0.5 sm:mb-2">
                  📝 <T k="tests.title" />
                </h1>
                <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto hidden sm:block">
                  <T k="features.tests.desc" />
                </p>
                {/* Quick badges showing both test types - Clickable */}
                <div className="flex flex-wrap items-center justify-center gap-2 mt-2 sm:mt-3">
                  {/* Practice Badge - Compact, Clickable */}
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.querySelector('[data-practice-section]');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 backdrop-blur-sm border border-emerald-400/40 shadow-sm cursor-pointer transition-all hover:scale-105 hover:bg-emerald-500/30 hover:border-emerald-400/70 active:scale-95"
                    data-testid="hero-badge-practice"
                    aria-label={getText(TEXTS.gotoPractice)}
                  >
                    <FileQuestion className="w-3 h-3 text-emerald-300" strokeWidth={2.5} />
                    <span className="text-[10px] sm:text-xs text-emerald-100 font-bold">
                      {getText(TEXTS.badgePractice)}
                    </span>
                    <span className="text-white font-black text-xs sm:text-sm">30</span>
                  </button>

                  {/* Final Exam Badge - Premium Compact, Clickable */}
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.querySelector('[data-slutprov-section]');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="relative inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 border border-amber-300/60 shadow-lg shadow-amber-500/30 overflow-hidden cta-auto-pulse cursor-pointer transition-all hover:scale-105 hover:shadow-amber-500/50 active:scale-95"
                    data-testid="hero-badge-slutprov"
                    aria-label={getText(TEXTS.gotoFinal)}
                  >
                    <span className="absolute inset-0 cta-auto-shine bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12 pointer-events-none w-1/2"></span>
                    <Crown className="relative w-3 h-3 text-white" strokeWidth={2.5} />
                    <span className="relative text-[10px] sm:text-xs text-white font-bold">
                      {getText(TEXTS.badgeFinal)}
                    </span>
                    <span className="relative text-white font-black text-xs sm:text-sm drop-shadow">{finalSpec.n}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Info Bar - Connected to Hero */}
            <div className="bg-white dark:bg-slate-800 rounded-b-2xl sm:rounded-b-3xl shadow-lg mb-2 sm:mb-3">
              <div className="flex justify-center items-center gap-5 sm:gap-12 pt-2.5 pb-1.5 sm:py-5 px-4">
                <div className="text-center">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-1 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md">
                    <FileQuestion className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="text-base sm:text-xl font-bold" dir="ltr" data-testid="info-questions"><span className="text-emerald-600 dark:text-emerald-400">30</span><span className="text-slate-300 mx-1">/</span><span className="text-amber-500">{finalSpec.n}</span></div>
                  <div className="text-[9px] sm:text-xs text-slate-500 dark:text-slate-400">{t('tests_questions')}</div>
                </div>
                <div className="w-px h-10 sm:h-12 bg-slate-200 dark:bg-slate-700"></div>
                <div className="text-center">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-1 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md">
                    <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="text-base sm:text-xl font-bold" dir="ltr" data-testid="info-minutes"><span className="text-emerald-600 dark:text-emerald-400">25</span><span className="text-slate-300 mx-1">/</span><span className="text-amber-500">50</span></div>
                  <div className="text-[9px] sm:text-xs text-slate-500 dark:text-slate-400">{getText(TEXTS.min)}</div>
                </div>
                <div className="w-px h-10 sm:h-12 bg-slate-200 dark:bg-slate-700"></div>
                <div className="text-center">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-1 rounded-lg sm:rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md">
                    <Trophy className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="text-base sm:text-xl font-bold text-slate-900 dark:text-slate-100">{Math.round((finalSpec.p / (finalSpec.n - (finalSpec.n === 50 ? 4 : 5))) * 100)}%</div>
                  <div className="text-[9px] sm:text-xs text-slate-500 dark:text-slate-400">{getText(TEXTS.pass)}</div>
                </div>
              </div>
              <div className="flex items-center justify-center gap-4 pb-2 text-[10px] sm:text-xs font-semibold" data-testid="info-legend">
                <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400"><span className="w-2 h-2 rounded-full bg-emerald-500" />{getText(TEXTS.badgePractice)}</span>
                <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400"><span className="w-2 h-2 rounded-full bg-amber-500" />{getText(TEXTS.badgeFinal)}</span>
              </div>
            </div>

            {/* Slutprov Promo Banner - يلفت الانتباه لوجود الامتحان النهائي */}
            {availableTests.some(t => t.isFinalExam) && (
              <button
                type="button"
                onClick={() => {
                  const slutprovHeader = document.querySelector('[data-slutprov-section]');
                  if (slutprovHeader) slutprovHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                data-testid="slutprov-banner-btn"
                className="w-full mb-4 sm:mb-6 group relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-3 sm:p-4 shadow-lg shadow-amber-500/40 hover:shadow-xl hover:shadow-amber-500/60 hover:scale-[1.01] transition-all duration-300 ring-2 ring-amber-300/40"
              >
                {/* Decorative shine */}
                <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 pointer-events-none"></span>
                <div className="relative z-10 flex items-center justify-between gap-3 sm:gap-4">
                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-full bg-white/25 backdrop-blur-md flex items-center justify-center ring-2 ring-white/40 shadow-inner">
                      <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <div className="text-left rtl:text-right min-w-0">
                      <div className="text-white font-black text-xs sm:text-base drop-shadow uppercase tracking-wide">
                        {getText(TEXTS.finalExamTitleEmoji)}
                      </div>
                      <div className="text-white/95 text-[10px] sm:text-xs font-medium mt-0.5 truncate">
                        {getText(TEXTS.finalExamSubtitle).replace('{n}', finalSpec.n).replace('{p}', finalSpec.p)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="bg-white/25 backdrop-blur-md text-white text-[10px] sm:text-xs font-black px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full border border-white/30 shadow-sm whitespace-nowrap">
                      {availableTests.filter(t => t.isFinalExam).length} {getText(TEXTS.examsCount)}
                    </span>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                    </svg>
                  </div>
                </div>
              </button>
            )}

            {/* Current license badge + prominent "Change section" button — hidden entirely
                for regular users when Taxi is disabled (single-license mode). */}
            {showTaxiCard && (
            <div className="bg-white dark:bg-slate-900 border-x border-slate-200 dark:border-slate-800 px-4 py-3 sm:py-4 flex items-center justify-between gap-3 flex-wrap">
              <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm text-white shadow-md ${
                licenseType === 'taxi'
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500'
                  : 'bg-gradient-to-r from-emerald-500 to-teal-500'
              }`} data-testid="current-license-badge">
                <span className="text-base">{licenseType === 'taxi' ? '🚕' : '🚗'}</span>
                <span>{licenseType === 'taxi' ? 'Taxikort' : 'B-körkort'}</span>
              </span>
              {showTaxiCard && (
              <button
                type="button"
                onClick={() => { setLicenseChosen(false); setTaxiSubCategory(null); }}
                data-testid="change-license-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100 font-bold text-xs sm:text-sm transition-all border-2 border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500 hover:shadow-md active:scale-95"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="1 4 1 10 7 10"></polyline>
                  <polyline points="23 20 23 14 17 14"></polyline>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                </svg>
                <span>{i18n.language === 'ar' ? 'تغيير القسم' : i18n.language === 'sv' ? 'Byt körkortstyp' : 'Change section'}</span>
              </button>
              )}
              {licenseType === 'taxi' && availableTests.length === 0 && (
                <p className="w-full text-center text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-2" data-testid="taxi-empty-hint">
                  {t('tests.license.taxiComingSoon', 'Taxifrågor kommer snart. Administratören lägger till dem inom kort.')}
                </p>
              )}
            </div>
            )}

            {/* Test Cards Section */}
            <div className="px-0 sm:px-0" data-practice-section>
              <h2 className="text-sm sm:text-lg font-bold text-slate-900 dark:text-slate-100 mb-3 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
                {getText(TEXTS.chooseTest)}
              </h2>
              
              {/* Scroll indicator for mobile */}
              <div className="flex items-center justify-center gap-2 mb-3 text-slate-500 text-xs animate-bounce sm:hidden">
                <span>👇</span>
                <span>{getText(TEXTS.scrollDown)}</span>
                <span>👇</span>
              </div>
              
              {loading || !testsInitialized ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-3" data-testid="tests-skeleton">
                  {Array.from({ length: 16 }).map((_, i) => (
                    <div key={i} className="aspect-[3/4] rounded-xl sm:rounded-2xl bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 dark:from-slate-800 dark:via-slate-700 dark:to-slate-800 animate-pulse" />
                  ))}
                </div>
              ) : availableTests.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-700">
                  <FileQuestion className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-600 dark:text-slate-400">
                    {getText(TEXTS.noTestsAvailable)}
                  </p>
                </div>
              ) : (
                <>
                  {/* Regular Tests Grid */}
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2 sm:gap-3">
                    {availableTests.filter(t => !t.isFinalExam).map((test, index) => {
                      const icons = ['🎯', '🚦', '🛑', '⚠️', '🚸', '⛔', '🚗', '🔄', '➡️', '🚧'];
                      const gradients = [
                        'from-emerald-400 via-emerald-500 to-teal-600',
                        'from-rose-400 via-rose-500 to-red-600',
                        'from-amber-400 via-amber-500 to-orange-600',
                        'from-blue-400 via-blue-500 to-indigo-600',
                        'from-purple-400 via-purple-500 to-violet-600',
                        'from-cyan-400 via-cyan-500 to-teal-600',
                        'from-pink-400 via-pink-500 to-rose-600',
                        'from-lime-400 via-lime-500 to-green-600',
                        'from-orange-400 via-orange-500 to-red-600',
                        'from-indigo-400 via-indigo-500 to-purple-600'
                      ];
                      const isFree = test.isFreeTest;
                      const icon = isFree ? '🎯' : icons[(index) % icons.length];
                      const gradient = isFree ? 'from-emerald-400 via-emerald-500 to-teal-600' : gradients[(index) % gradients.length];
                      const needsAccount = test.requiresRegistration && !user;
                      const isLocked = test.isLocked && !needsAccount;
                      
                      return (
                        <div 
                          key={test.id}
                          onClick={() => isLocked ? setShowAuthPromptModal(true) : needsAccount ? setShowRegisterPrompt(true) : loadingTestId ? null : startTest(test.id)}
                          data-testid={`test-card-${index}`}
                          onMouseEnter={() => !isLocked && prefetchDetail('test', test.id)}
                          className={`group cursor-pointer ${loadingTestId === test.id ? 'pointer-events-none' : ''}`}
                        >
                          <div className={`relative rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 ${isLocked ? 'opacity-40 grayscale dark:opacity-30 shadow-sm' : 'shadow-md hover:shadow-xl ring-1 ring-white/20 dark:ring-emerald-500/30'} ${loadingTestId === test.id ? 'ring-2 ring-emerald-400 scale-110 shadow-2xl shadow-emerald-500/30' : ''}`}>
                            {loadingTestId === test.id && (
                              <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/20 backdrop-blur-[1px] rounded-xl">
                                <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                              </div>
                            )}
                            {isLocked && (
                              <div className="absolute top-1 right-1 sm:top-1.5 sm:right-1.5 z-20">
                                <div className="bg-gradient-to-br from-amber-300 to-amber-500 rounded-full p-1 sm:p-1.5 shadow-lg transform rotate-12">
                                  <Crown className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" strokeWidth={2.5} />
                                </div>
                              </div>
                            )}
                            <div className={`aspect-square bg-gradient-to-br ${gradient} p-3 sm:p-4 flex flex-col items-center justify-center relative`}>
                              {isLocked && <div className="absolute inset-0 bg-black/30 dark:bg-black/50 z-[1]"></div>}
                              {(isFree || test.requiresRegistration) && !isLocked && (
                                <div className="absolute top-1 left-1 sm:top-1.5 sm:left-1.5 z-20">
                                  <div className="bg-white/90 dark:bg-black/50 rounded-full px-1.5 py-0.5">
                                    <span className="text-[7px] sm:text-[8px] font-bold text-emerald-600 dark:text-emerald-400">{getText(TEXTS.freeBadge)}</span>
                                  </div>
                                </div>
                              )}
                              <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -mr-8 -mt-8"></div>
                              <div className="absolute bottom-0 left-0 w-12 h-12 bg-white/10 rounded-full -ml-6 -mb-6"></div>
                              <span className="text-3xl sm:text-4xl mb-1 drop-shadow-lg group-hover:scale-110 transition-transform relative z-10">{icon}</span>
                              <div className="bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full relative z-10">
                                <span className="text-white text-[10px] sm:text-xs font-bold">#{index + 1}</span>
                              </div>
                            </div>
                            <div className={`px-2 py-2 sm:px-3 sm:py-2.5 ${isLocked ? 'bg-slate-200 dark:bg-slate-900' : 'bg-white dark:bg-slate-800'}`}>
                              <h3 className={`font-semibold text-[10px] sm:text-xs line-clamp-1 text-center mb-1 ${isLocked ? 'text-slate-400 dark:text-slate-600' : 'text-slate-800 dark:text-slate-200'}`}>
                                {getTestName(test.name, index + 1, test)}
                              </h3>
                              <div className="flex items-center justify-center gap-1.5 text-[8px] sm:text-[10px]">
                                <span className={`px-1.5 py-0.5 rounded-full font-medium ${isLocked ? 'bg-slate-300 dark:bg-slate-800 text-slate-400 dark:text-slate-600' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                                  {test.questionsCount} {getText(TEXTS.questionShort)}
                                </span>
                                <span className={`px-1.5 py-0.5 rounded-full font-medium ${isLocked ? 'bg-slate-300 dark:bg-slate-800 text-slate-400 dark:text-slate-600' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                                  {test.duration} {getText(TEXTS.minuteShort)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Scroll indicator: hint user about more cards below */}
                  {availableTests.some(t => t.isFinalExam) && (
                    <button
                      type="button"
                      onClick={() => {
                        const slutprovHeader = document.querySelector('[data-slutprov-section]');
                        if (slutprovHeader) slutprovHeader.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      data-testid="scroll-to-slutprov-btn"
                      className="mt-6 sm:mt-8 mx-auto flex flex-col items-center gap-1.5 group cursor-pointer animate-bounce-slow"
                    >
                      <div className="text-xs sm:text-sm font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 shadow-sm group-hover:shadow-md transition-all">
                        <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.5} />
                        <span>{getText(TEXTS.finalExamBelow)}</span>
                        <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4" strokeWidth={2.5} />
                      </div>
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 dark:text-amber-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                      </svg>
                    </button>
                  )}

                  {/* Final Exams Section */}
                  {availableTests.some(t => t.isFinalExam) && (
                    <div className="mt-10 sm:mt-12" data-slutprov-section></div>
                  )}
                  {availableTests.some(t => t.isFinalExam) && (
                    <div className="mt-2">
                      {/* Premium Section Header */}
                      <div className="relative mb-6 sm:mb-8">
                        <div className="flex items-center justify-center gap-2 sm:gap-4">
                          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-400 to-amber-500 dark:via-amber-600"></div>
                          <div className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 shadow-lg shadow-amber-500/40 ring-2 ring-amber-300/50 dark:ring-amber-700/50">
                            <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-white drop-shadow" strokeWidth={2.5} />
                            <h2 className="text-xs sm:text-sm font-black text-white tracking-wider uppercase drop-shadow">
                              {getText(TEXTS.finalExamHeader)}
                            </h2>
                            <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-white drop-shadow" strokeWidth={2.5} />
                          </div>
                          <div className="h-px flex-1 bg-gradient-to-l from-transparent via-amber-400 to-amber-500 dark:via-amber-600"></div>
                        </div>
                        <p className="text-center text-[10px] sm:text-xs text-amber-700 dark:text-amber-400 mt-2 font-medium tracking-wide">
                          {getText(TEXTS.finalExamTagline).replace('{n}', finalSpec.n)}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                        {availableTests.filter(t => t.isFinalExam).map((test, i) => {
                          const isLocked = test.isLocked;
                          // Rotating premium gradients for each Slutprov
                          const premiumGradients = [
                            'from-amber-500 via-orange-500 to-red-500',
                            'from-purple-600 via-pink-500 to-rose-500',
                            'from-blue-600 via-indigo-600 to-violet-600',
                            'from-emerald-500 via-teal-500 to-cyan-600',
                            'from-rose-500 via-red-500 to-orange-500',
                            'from-violet-600 via-purple-600 to-fuchsia-600',
                            'from-slate-700 via-slate-800 to-slate-900',
                            'from-yellow-500 via-amber-500 to-orange-600',
                            'from-cyan-500 via-blue-500 to-indigo-600',
                          ];
                          const gradient = premiumGradients[i % premiumGradients.length];
                          const testNum = (test.name || '').match(/\d+/)?.[0] || (i + 1);
                          
                          return (
                            <div
                              key={test.id}
                              onClick={() => isLocked ? setShowAuthPromptModal(true) : (loadingTestId ? null : startTest(test.id))}
                              onMouseEnter={() => !isLocked && prefetchDetail('test', test.id)}
                              className={`group cursor-pointer ${loadingTestId === test.id ? 'pointer-events-none' : ''}`}
                              data-testid={`final-exam-card-${i}`}
                            >
                              <div className={`relative rounded-3xl overflow-hidden transition-all duration-500 hover:scale-[1.04] hover:-translate-y-1 ${isLocked ? 'opacity-60 grayscale' : 'shadow-xl hover:shadow-2xl ring-2 ring-white/30 dark:ring-amber-500/20 hover:ring-amber-400/60'} ${loadingTestId === test.id ? 'ring-2 ring-emerald-400 scale-110 shadow-2xl shadow-emerald-500/30' : ''}`}>
                                {loadingTestId === test.id && (
                                  <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/20 backdrop-blur-[1px] rounded-3xl">
                                    <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                                  </div>
                                )}
                                {/* Premium crown badge */}
                                {isLocked && (
                                  <div className="absolute top-3 right-3 z-30">
                                    <div className="relative">
                                      <div className="absolute inset-0 bg-amber-400 rounded-full blur-md opacity-60 animate-pulse"></div>
                                      <div className="relative bg-gradient-to-br from-amber-300 to-amber-600 rounded-full p-2 shadow-xl ring-2 ring-white/50">
                                        <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {/* Premium ribbon */}
                                {!isLocked && (
                                  <div className="absolute top-3 right-3 z-30">
                                    <div className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-[9px] sm:text-[10px] font-black px-2.5 py-1 rounded-full shadow-lg ring-1 ring-amber-300 flex items-center gap-1">
                                      <Sparkles className="w-3 h-3" />
                                      PREMIUM
                                    </div>
                                  </div>
                                )}

                                <div className={`bg-gradient-to-br ${gradient} p-5 sm:p-6 relative min-h-[180px] sm:min-h-[200px] flex flex-col justify-between`}>
                                  {isLocked && <div className="absolute inset-0 bg-black/40 z-[1]"></div>}
                                  
                                  {/* Decorative circles */}
                                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-12 -mt-12 blur-sm"></div>
                                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-10 -mb-10 blur-sm"></div>
                                  <div className="absolute top-1/2 right-4 w-16 h-16 bg-white/5 rounded-full blur"></div>
                                  
                                  {/* Grain texture overlay */}
                                  <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.3) 0%, transparent 40%)'}}></div>
                                  
                                  {/* Top: Big exam number */}
                                  <div className="relative z-10 flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/25 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-inner border border-white/30 group-hover:rotate-6 group-hover:scale-110 transition-all duration-500">
                                        <span className="text-3xl sm:text-4xl drop-shadow-lg">📜</span>
                                      </div>
                                      <div>
                                        <div className="text-white/70 text-[10px] sm:text-xs font-bold uppercase tracking-widest">
                                          {getText(TEXTS.examLabel)}
                                        </div>
                                        <div className="text-white font-black text-2xl sm:text-3xl leading-none drop-shadow-lg">#{testNum}</div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Middle: Title */}
                                  <div className="relative z-10 my-3">
                                    <h3 className="text-white font-bold text-base sm:text-lg drop-shadow-lg">
                                      {test.name}
                                    </h3>
                                    <p className="text-white/85 text-[11px] sm:text-xs mt-1 line-clamp-2">
                                      {getText(TEXTS.examSimDesc)}
                                    </p>
                                  </div>

                                  {/* Bottom: Stats badges */}
                                  <div className="relative z-10 flex flex-wrap gap-1.5 mt-2">
                                    <span className="bg-white/25 backdrop-blur-md text-white text-[10px] sm:text-xs font-black px-2.5 py-1 rounded-full border border-white/30 shadow-sm flex items-center gap-1">
                                      <span>📝</span>
                                      {test.questionCount || test.questionsCount || finalSpec.n} {getText(TEXTS.questionShort)}
                                    </span>
                                    <span className="bg-white/25 backdrop-blur-md text-white text-[10px] sm:text-xs font-black px-2.5 py-1 rounded-full border border-white/30 shadow-sm flex items-center gap-1">
                                      <span>⏱️</span>
                                      {test.duration || 50} {getText(TEXTS.minuteShort)}
                                    </span>
                                    <span className="bg-white/25 backdrop-blur-md text-white text-[10px] sm:text-xs font-black px-2.5 py-1 rounded-full border border-white/30 shadow-sm flex items-center gap-1">
                                      <span>🎯</span>
                                      80%
                                    </span>
                                  </div>
                                  
                                  {/* Shine effect on hover */}
                                  <div className="absolute -inset-full top-0 block transform -skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent h-full w-1/2 z-20 opacity-0 group-hover:opacity-100 group-hover:animate-[shine_1.2s_ease-in-out]"></div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Bottom counter */}
                  {availableTests.length > 12 && (
                    <div className="text-center mt-4 text-slate-500 text-xs sm:text-sm">
                      <p>{getText(TEXTS.totalTests)} {availableTests.length} {getText(TEXTS.tests)}</p>
                    </div>
                  )}
                </>
              )}
            </div>

          </div>
        </main>
        <div className="max-w-6xl mx-auto px-4 sm:px-6"><RelatedPages currentPage="tests" /></div>
        <Footer />
        <SubscribeModal />
        <AuthPromptModal />
        <RegisterPromptModal />
      </div>
    );
  }

  // Should the post-test subscribe CTA be shown for this user?
  // Per-license: hide the CTA when the user already holds an active
  // subscription for the license they just tested — a paying Taxi customer
  // shouldn't be nagged to buy Taxi again just because their B is still trial.
  const _justTestedLic = selectedTest?.licenseType === 'taxi' ? 'taxi' : 'b_korkort';
  const _hasActiveSubForTestedLic = _justTestedLic === 'taxi'
    ? (user?.taxi_subscription_status === 'active')
    : (user?.subscription_status === 'active' || user?.subscription_plan === 'lifetime');

  const shouldShowSubscribeCTA =
    testSubmitted &&
    results &&
    (selectedTest?.isFreeTest ||
      !user ||
      user?.subscription_status === 'trial' ||
      user?.subscription_status === 'expired') &&
    !_hasActiveSubForTestedLic &&
    !user?.is_admin &&
    user?.role !== 'super_admin';

  // Results screen
  if (testSubmitted && results) {
    const passed = typeof results.passed === 'boolean' ? results.passed : results.score_percentage >= 80;
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950" data-testid="test-results">
        <SEO page="tests" />
        <Navbar />
        <main className="flex-grow pt-20 pb-12">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white dark:bg-slate-800 rounded-3xl overflow-hidden border border-emerald-100/80 dark:border-slate-700 shadow-[0_18px_50px_-24px_rgba(5,150,105,0.45)] text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
              <ResultHero refEl={resultCardRef} passed={passed} results={results} unanswered={Math.max(0, questions.length - Object.keys(answers).filter((k) => answers[k] !== undefined && answers[k] !== null).length)} title={t('tests.results')} message={passed ? t('tests.passed') : t('tests.failed')} trialNote={results.trial_count > 0 ? getText(TEXTS.trialNote).replace('{n}', results.trial_count).replace('{p}', results.pass_count) : null} />
              <div className="px-4 sm:px-6 pt-3 pb-4">
              {/* 🎁 Hero CTA banner — placed RIGHT AFTER the score so mobile
                  users see the offer immediately, before any share buttons.
                  Animated to grab attention. */}
              {shouldShowSubscribeCTA && (
                <button
                  type="button"
                  data-testid="scroll-to-offer-hint"
                  onClick={() => setShowAuthPromptModal(true)}
                  className="w-full mb-3 mt-3 px-3 sm:px-4 py-3 rounded-2xl bg-gradient-to-r from-amber-400 via-orange-500 to-orange-500 hover:brightness-105 active:scale-[0.99] transition-[filter,transform] flex items-center justify-between gap-3 text-start shadow-[0_12px_28px_-12px_rgba(249,115,22,0.75)]"
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <span className="w-9 h-9 rounded-xl bg-white/25 ring-1 ring-white/40 text-white flex items-center justify-center flex-shrink-0"><Crown className="w-4 h-4" /></span>
                    <div className="min-w-0">
                      <div className="font-bold text-white text-sm sm:text-base leading-tight">
                        {(() => {
                          const lang = i18n.language || 'sv';
                          // Copy varies by license so we don't show B-körkort's
                          // "800+ frågor" hint on a Taxi results page.
                          const isTaxi = selectedTest?.licenseType === 'taxi';
                          if (isTaxi) {
                            return {
                              sv: 'Lås upp alla 480+ taxifrågor',
                              ar: 'افتح كل الـ 480+ سؤال تاكسي',
                              en: 'Unlock all 480+ taxi questions',
                              tr: '480+ taksi sorusunun kilidini aç',
                              fa: 'باز کردن 480+ سوال تاکسی',
                              so: "Fur dhammaan 480+ su'aal taxi",
                              pl: 'Odblokuj 480+ pytania taxi',
                              es: 'Desbloquea las 480+ preguntas de taxi',
                              de: 'Schalte 480+ Taxi-Fragen frei',
                            }[lang] || 'Unlock all 480+ taxi questions';
                          }
                          return {
                            sv: 'Prenumerera – lås upp alla 800+ frågor',
                            ar: 'اشترك وافتح كل الـ 800+ سؤال',
                            en: 'Subscribe – unlock all 800+ questions',
                            tr: '800+ sorunun kilidini aç',
                            fa: 'باز کردن ۸۰۰+ سوال',
                            so: 'Fur dhammaan 800+ su\'aalood',
                            pl: 'Odblokuj 800+ pytań',
                            es: 'Desbloquea las 800+ preguntas',
                            de: 'Abonnieren – 800+ Fragen freischalten',
                          }[lang] || 'Subscribe – unlock all 800+ questions';
                        })()}
                      </div>
                      <div className="text-xs text-white/90 mt-0.5">
                        {(() => {
                          const lang = i18n.language || 'sv';
                          return {
                            sv: 'Alla prov · förklaring till varje fråga',
                            ar: 'كل الاختبارات · شرح لكل سؤال',
                            en: 'All tests · explanation for every question',
                            tr: 'Tüm testler · her soruya açıklama',
                            fa: 'همه آزمون‌ها · توضیح هر سوال',
                            so: "Dhammaan imtixaannada · sharraxaad su'aal kasta",
                            pl: 'Wszystkie testy · wyjaśnienie każdego pytania',
                            es: 'Todos los exámenes · explicación de cada pregunta',
                            de: 'Alle Tests · Erklärung zu jeder Frage',
                          }[lang] || 'All tests · explanation for every question';
                        })()}
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-white flex-shrink-0 rtl:rotate-180" />
                </button>
              )}

              {/* Share buttons - outside capturable area */}
              <div className="flex items-center justify-between gap-2 mb-3 mt-1 rounded-2xl border border-slate-100 dark:border-slate-700 px-3 py-2" data-testid="share-buttons">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 shrink-0">{getText(TEXTS.shareResult)}</span>
                <div className="flex items-center gap-1.5">
                <button
                  onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(getText(TEXTS.shareText).replace('{score}', results.score_percentage) + ' https://drivemaster.se')}`, '_blank')}
                  className="w-8 h-8 rounded-lg hover:-translate-y-0.5 transition-transform bg-[#25D366] hover:bg-[#1fb855] flex items-center justify-center text-white transition-colors"
                  data-testid="share-whatsapp"
                  title="WhatsApp"
                >
                  <svg className="w-4.5 h-4.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                </button>
                <button
                  onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://drivemaster.se')}&quote=${encodeURIComponent(getText(TEXTS.shareText).replace('{score}', results.score_percentage))}`, '_blank')}
                  className="w-8 h-8 rounded-lg hover:-translate-y-0.5 transition-transform bg-[#1877F2] hover:bg-[#166fe5] flex items-center justify-center text-white transition-colors"
                  data-testid="share-facebook"
                  title="Facebook"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </button>
                <button
                  onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(getText(TEXTS.shareText).replace('{score}', results.score_percentage))}&url=${encodeURIComponent('https://drivemaster.se')}`, '_blank')}
                  className="w-8 h-8 rounded-lg hover:-translate-y-0.5 transition-transform bg-black hover:bg-gray-800 flex items-center justify-center text-white transition-colors"
                  data-testid="share-twitter"
                  title="X (Twitter)"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </button>
                <button
                  onClick={() => {
                    const text = getText(TEXTS.shareText).replace('{score}', results.score_percentage) + ' https://drivemaster.se';
                    navigator.clipboard.writeText(text);
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2000);
                  }}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${linkCopied ? 'bg-emerald-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600'}`}
                  data-testid="share-copy"
                  title={getText(TEXTS.copied)}
                >
                  {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
                <button
                  onClick={downloadResultImage}
                  className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 flex items-center justify-center text-slate-600 dark:text-slate-300 transition-colors"
                  data-testid="share-download"
                  title={i18n.language === 'sv' ? 'Ladda ner bild' : i18n.language === 'ar' ? 'تحميل الصورة' : 'Download'}
                >
                  <Download className="w-4 h-4" />
                </button>
                </div>
              </div>

              {(() => {
                const seq = availableTests.filter((t) => !!t.isFinalExam === !!selectedTest?.isFinalExam);
                const i = seq.findIndex((t) => t.id === selectedTest?.id);
                const next = i >= 0 ? seq[i + 1] : null;
                if (!next) return null;
                const needsAccount = next.requiresRegistration && !user;
                const locked = next.isLocked && !needsAccount;
                const label = ({ sv: 'Nästa prov', ar: 'الاختبار التالي', en: 'Next test', tr: 'Sonraki test', fa: 'آزمون بعدی', so: 'Imtixaanka xiga', pl: 'Następny test', es: 'Siguiente prueba', de: 'Nächster Test' })[i18n.language] || 'Next test';
                const go = async () => {
                  if (nextBusy) return;
                  if (needsAccount) { setShowRegisterPrompt(true); return; }
                  if (locked) { navigate('/priser'); return; }
                  setNextBusy(true);
                  try { await startTest(next.id); } finally { setNextBusy(false); window.scrollTo({ top: 0 }); }
                };
                return (
                  <Button onClick={go} disabled={nextBusy} data-testid="next-test-btn"
                    className="group relative w-full mb-2 min-h-12 h-auto py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold text-[15px] whitespace-normal leading-tight shadow-[0_10px_24px_-10px_rgba(16,185,129,0.9)] active:scale-[0.98] transition-transform disabled:opacity-100">
                    {(locked || needsAccount) && <Lock className="w-4 h-4 shrink-0 opacity-90" />}
                    <span className="min-w-0">{label}<span className="font-medium opacity-80"> · {next.isFinalExam ? `${getText(TEXTS.finalExamHeader)} ${seq.indexOf(next) + 1}` : getTestName(next.name, seq.indexOf(next) + 1, next)}</span></span>
                    {nextBusy
                      ? <span className="w-4 h-4 shrink-0 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                      : <ArrowRight className="w-5 h-5 shrink-0 transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />}
                  </Button>
                );
              })()}
              {/* زر إعادة الاختبار - يختفي إذا انتهت الأسئلة المجانية */}
              <div className="grid grid-cols-2 gap-2">
                {((user?.subscription_status !== 'trial' && user?.subscription_status !== 'expired') || (user?.subscription_status === 'trial' && user?.questions_remaining > 0 && user?.questions_answered < user?.questions_limit)) ? (
                  <Button
                    onClick={async () => { if (retrying) return; setRetrying(true); try { await startTest(selectedTest?.id); } finally { setRetrying(false); window.scrollTo({ top: 0 }); } }}
                    disabled={retrying}
                    data-busy={retrying}
                    variant="outline"
                    className="relative overflow-hidden disabled:opacity-100 data-[busy=true]:scale-[0.97] min-h-11 h-auto py-2 text-sm min-w-0 whitespace-normal leading-tight rounded-xl border-emerald-200 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 px-3 font-semibold active:scale-[0.98] transition-transform"
                    data-testid="try-again-btn"
                  >
                    {retrying ? <span className="w-4 h-4 shrink-0 rounded-full border-2 border-emerald-200 border-t-emerald-600 animate-spin" /> : <RotateCcw className="w-4 h-4 shrink-0" />}
                    {retrying ? (({ sv: 'Laddar…', ar: 'جارٍ التحميل…', en: 'Loading…', tr: 'Yükleniyor…', fa: 'در حال بارگذاری…', so: 'Waa la rarayaa…', pl: 'Ładowanie…', es: 'Cargando…', de: 'Lädt…' })[i18n.language] || 'Loading…') : <T k="tests.tryAgain" />}
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/priser')}
                    className="min-h-11 h-auto py-2 text-sm min-w-0 whitespace-normal leading-tight rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-3 font-semibold"
                    data-testid="subscribe-btn"
                  >
                    <Crown className="w-4 h-4 shrink-0" />
                    {getText(TEXTS.subscribeToContinue)}
                  </Button>
                )}
                
                {/* زر الرجوع للبطاقات */}
                <Button
                  onClick={() => {
                    // Reset all state to go back to tests list
                    setResults(null);
                    setSelectedTest(null);
                    setCurrentIndex(0);
                    setAnswers({});
                    setQuestions([]);
                    setTestStarted(false);
                    setTestSubmitted(false);
                    setWrongAnswers({});
                    setExpandedExplanations({});
                    // تتبع المغادرة بعد النتيجة
                    setTestTrackingId(null);
                  }}
                  variant="outline"
                  className="min-h-11 h-auto py-2 text-sm min-w-0 whitespace-normal leading-tight rounded-xl border-slate-200 text-slate-700 dark:text-slate-200 hover:bg-emerald-50 px-3 font-semibold active:scale-[0.98] transition-transform"
                  data-testid="back-to-tests-btn"
                >
                  <ArrowRight className="w-4 h-4 shrink-0 rotate-180 rtl:rotate-0" />
                  {getText(TEXTS.backToCards)}
                </Button>
              </div>
              </div>
            </div>


            <SubscribeNudge open={!!user && showNudge} lang={i18n.language} license={selectedTest?.licenseType === 'taxi' ? 'taxi' : 'b'}
              questionCount={selectedTest?.licenseType === 'taxi' ? '480+' : '800+'}
              lockedTests={Math.max(1, availableTests.filter((x) => x.isLocked || (!x.isFreeTest && !x.requiresRegistration)).length)}
              onClose={() => { setShowNudge(false); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 60); }}
              onUnlock={() => { setShowNudge(false); setShowAuthPromptModal(true); }} />
            <GuestResultModal onUnlock={() => { setShowGuestResult(false); setShowAuthPromptModal(true); }} open={!user && showGuestResult} onClose={() => { setShowGuestResult(false); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 60); }} lang={i18n.language}
              score={results.score_percentage} passed={passed} returnTo={location.pathname} />
            {!user && <ReviewGate lang={i18n.language} wrong={Math.max(0, results.total_questions - results.correct_answers - Math.max(0, questions.length - Object.keys(answers).filter((k) => answers[k] !== undefined && answers[k] !== null).length)) || (results.total_questions - results.correct_answers)} returnTo={location.pathname} />}

            {/* Show answers */}
            {user && <div className="mt-8 space-y-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4">
                {getText(TEXTS.reviewAnswers)}
              </h2>
              {results.results.map((result, idx) => {
                const question = questions.find(q => q.id === result.question_id);
                if (!question) return null;
                const hasExplanation = result.explanation && (result.explanation[i18n.language] || result.explanation.en);
                // Auto-expand explanation for wrong answers
                const showExplanation = !result.is_correct || expandedExplanations[result.question_id];
                
                return (
                  <div key={result.question_id} className={`bg-white dark:bg-slate-800 rounded-xl p-6 border-2 ${result.is_correct ? 'border-emerald-200 dark:border-emerald-800' : 'border-red-200 dark:border-red-800'}`}>
                    <div className="flex items-start gap-3 mb-3">
                      {result.is_correct ? (
                        <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900 dark:text-slate-100 mb-2">
                          {idx + 1}. {question.question[i18n.language] || question.question.en}
                        </p>
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`font-medium ${result.is_correct ? 'text-emerald-600' : 'text-red-600'}`}>
                            {result.is_correct 
                              ? getText(TEXTS.correct)
                              : getText(TEXTS.incorrect)
                            }
                          </span>
                          {result.is_trial && (
                            <span className="ms-2 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300" data-testid="trial-question-badge">{getText(TEXTS.trialQuestion)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {hasExplanation && (
                      <div className="ms-9">
                        {result.is_correct ? (
                          /* For correct answers, show toggle button */
                          <button
                            onClick={() => toggleExplanation(result.question_id)}
                            className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                          >
                            {expandedExplanations[result.question_id] ? (
                              <>
                                <XCircle className="w-4 h-4" />
                                {getText(TEXTS.hideExplanation)}
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4" />
                                {getText(TEXTS.showExplanation)}
                              </>
                            )}
                          </button>
                        ) : null}
                        
                        {showExplanation && (
                          <div className={`mt-3 p-4 rounded-lg ${result.is_correct ? 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800' : 'bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700'}`}>
                            <div className="flex items-start gap-2">
                              <span className="text-lg">💡</span>
                              <div>
                                <span className="font-bold text-slate-800 dark:text-slate-200 block mb-1">
                                  {getText(TEXTS.explanation)}:
                                </span>
                                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                                  {result.explanation[i18n.language] || result.explanation.en}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>}
          </div>
        </main>
        <Footer />
        <SubscribeModal />
        <AuthPromptModal />
        <RegisterPromptModal />
      </div>
    );
  }

  // Test in progress
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-emerald-50/20" data-testid="test-in-progress">
      <SEO page="tests" />
      <FAQSchema language={i18n.language} page="tests" />
      <QuizSchema language={i18n.language} />
      <PageBreadcrumbs page="tests" language={i18n.language} />
      <Navbar />
      <main className="flex-grow pt-16 pb-8 sm:pb-12">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-6">
            {/* Main Content - Question Area */}
            <div className="flex-1 min-w-0">
              
              {/* Clean Header - Mobile Optimized */}
              <div data-testid="question-header" className="flex flex-row items-center justify-between gap-2 sm:gap-3 mb-2 sm:mb-3 bg-white rounded-xl sm:rounded-2xl px-3 py-1.5 sm:p-3 shadow-lg border border-slate-200">
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-xs sm:text-lg shadow-lg">
                    {currentIndex + 1}
                  </div>
                  <div dir={isRTL ? 'rtl' : 'ltr'}>
                    <div className="text-sm sm:text-lg font-bold text-slate-900 leading-tight">
                      {currentIndex + 1} / {questions.length}
                    </div>
                    <div className="text-[10px] sm:text-sm text-slate-600 leading-tight">
                      {Math.round(progress)}%
                    </div>
                  </div>
                </div>
                
                {/* Timer */}
                <div className={`flex items-center gap-1.5 sm:gap-3 px-2.5 sm:px-5 py-1 sm:py-3 rounded-lg sm:rounded-xl shadow-lg ${timeRemaining < 300 ? 'bg-gradient-to-r from-red-500 to-red-600' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}>
                  <Clock className="w-3.5 h-3.5 sm:w-6 sm:h-6 text-white" />
                  <div>
                    <div className="text-sm sm:text-2xl font-black text-white leading-none">
                      {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
                    </div>
                    <div className="text-[10px] sm:text-xs text-white/90 mt-0.5 hidden sm:block">
                      {getText(TEXTS.left)}
                    </div>
                  </div>
                </div>
              </div>

          {/* Question Card */}
          {currentQuestion && (
            <div 
              key={`${currentQuestion.id}-${languageKey}`}
              className="bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-6 shadow-xl border-2 border-slate-100 relative" 
              data-testid="question-card"
            >
              {(currentQuestion.image_url || (currentQuestion.image && currentQuestion.image !== 'None')) && currentQuestion.image_url !== '' && (
                <>
                  <img
                    src={(() => {
                      const imgSrc = currentQuestion.image_url || currentQuestion.image;
                      if (!imgSrc || imgSrc === 'None' || imgSrc === '') return '';
                      // No cache-busting: let browser cache for instant display
                      if (imgSrc.startsWith('/api/uploads/')) return imgSrc;
                      if (imgSrc.startsWith('http')) return imgSrc;
                      return imgSrc;
                    })()}
                    alt={`Körkortsfråga - ${getQuestionText(currentQuestion, 'sv')?.substring(0, 80) || 'Teoriprov'}`}
                    className="w-full max-h-44 sm:max-h-64 object-contain rounded-lg sm:rounded-xl mb-3 sm:mb-6 border-2 border-slate-200 bg-slate-50 cursor-zoom-in"
                    loading="eager"
                    fetchpriority="high"
                    decoding="async"
                    onClick={() => {
                      const imgSrc = currentQuestion.image_url || currentQuestion.image;
                      if (!imgSrc || imgSrc === 'None' || imgSrc === '') return;
                      setZoomedImage(imgSrc);
                    }}
                    data-testid="question-image"
                  />
                  {zoomedImage && (
                    <div
                      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-zoom-out"
                      onClick={() => setZoomedImage(null)}
                      data-testid="image-zoom-overlay"
                    >
                      <img
                        src={zoomedImage}
                        alt="Zoomed"
                        className="max-w-full max-h-full object-contain rounded-xl"
                      />
                    </div>
                  )}
                </>
              )}

              {/* Question video (optional). Same URL contract as image_url —
                  either an absolute URL or a relative /api/uploads/videos/… path. */}
              {currentQuestion.video_url && currentQuestion.video_url !== '' && (
                <video
                  key={currentQuestion.video_url}
                  src={currentQuestion.video_url.startsWith('http')
                    ? currentQuestion.video_url
                    : currentQuestion.video_url}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full max-h-56 sm:max-h-72 rounded-lg sm:rounded-xl mb-3 sm:mb-6 border-2 border-slate-200 bg-black"
                  data-testid="question-video"
                >
                  Your browser does not support HTML5 video.
                </video>
              )}
              <div className="flex items-start gap-2 mb-3 sm:mb-6">
                <h2 className="question-text text-sm sm:text-xl font-bold text-slate-900 leading-snug flex-1">
                  {getQuestionText(currentQuestion, i18n.language)}
                </h2>
                {isTTSSupported() && (
                  <SpeakButton
                    phase={speakingId === `q-${currentQuestion.id}` ? speechPhase : 'idle'}
                    onClick={() => handleSpeak(`q-${currentQuestion.id}`, getQuestionText(currentQuestion, i18n.language))}
                    label={i18n.language === 'ar' ? 'استمع للسؤال' : i18n.language === 'sv' ? 'Lyssna på frågan' : 'Listen to question'}
                    stopLabel={i18n.language === 'ar' ? 'إيقاف' : i18n.language === 'sv' ? 'Stoppa' : 'Stop'}
                    testId="speak-question-btn"
                  />
                )}
              </div>

              <div className="space-y-1 sm:space-y-2">
                {(() => {
                  const questionId = currentQuestion.id || currentQuestion._id;
                  const shuffleData = shuffledOptionsMap[questionId];
                  // استخدام الدالة المساعدة للحصول على الخيارات بالصيغة الصحيحة
                  const originalOptions = getOptionsForLanguage(currentQuestion.options, i18n.language);
                  
                  // استخدام الفهارس المخلوطة لعرض الخيارات بترتيب مختلف
                  const displayIndices = shuffleData?.shuffledIndices || originalOptions.map((_, i) => i);
                  
                  const correctAnswerIndex = shuffleData 
                    ? shuffleData.newCorrectAnswer 
                    : (currentQuestion.correctAnswer ?? currentQuestion.correct_answer ?? 0);
                  
                  return displayIndices.map((originalIdx, displayIdx) => {
                    const letters = ['A', 'B', 'C', 'D'];
                    const isSelected = answers[currentQuestion.id] === displayIdx;
                    const hasWrongAnswer = wrongAnswers[currentQuestion.id] !== undefined;
                    const isWrongSelected = wrongAnswers[currentQuestion.id] === displayIdx;
                    const isCorrectAnswer = displayIdx === correctAnswerIndex;
                    const showCorrect = isSelected && isCorrectAnswer;
                    const showWrong = isSelected && isWrongSelected;
                    // Show correct answer when user selected wrong
                    const highlightCorrect = hasWrongAnswer && isCorrectAnswer && !isSelected;
                    
                    // Get option text from original options using the original index
                    const optionText = originalOptions[originalIdx] || '';
                    
                    return (
                      <div key={displayIdx} className="flex items-stretch gap-2">
                        <button
                          onClick={() => selectAnswer(currentQuestion.id, displayIdx)}
                          className={`group flex-1 min-w-0 px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-lg sm:rounded-2xl border-2 text-start transition-all duration-300 hover:shadow-lg ${
                            showWrong
                              ? 'border-red-500 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-500/20 dark:to-red-500/10 dark:border-red-400 shadow-md'
                              : showCorrect
                                ? 'border-emerald-500 bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-500/20 dark:to-emerald-500/10 dark:border-emerald-400 shadow-md'
                                : highlightCorrect
                                  ? 'border-emerald-500 bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-500/25 dark:to-emerald-500/15 dark:border-emerald-400 shadow-md ring-2 ring-emerald-400 ring-offset-2 dark:ring-offset-slate-900'
                                  : isSelected 
                                    ? 'border-emerald-500 bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-500/20 dark:to-emerald-500/10 dark:border-emerald-400 shadow-md' 
                                    : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 bg-white dark:bg-slate-800/50'
                          }`}
                          data-testid={`option-${displayIdx}`}
                        >
                          <div className="flex items-center gap-2 sm:gap-4">
                            <div className={`option-letter w-7 h-7 sm:w-10 sm:h-10 rounded-md sm:rounded-lg flex items-center justify-center font-bold text-xs sm:text-base transition-all flex-shrink-0 ${
                              showWrong
                                ? 'bg-red-500 text-white'
                                : showCorrect || highlightCorrect
                                  ? 'bg-emerald-500 text-white'
                                  : isSelected 
                                    ? 'bg-emerald-500 text-white' 
                                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50 group-hover:text-emerald-600'
                            }`}>
                              {showWrong ? <XCircle className="w-3.5 h-3.5 sm:w-5 sm:h-5" /> : (showCorrect || highlightCorrect) ? <CheckCircle className="w-3.5 h-3.5 sm:w-5 sm:h-5" /> : letters[displayIdx]}
                            </div>
                            <span className={`option-text font-semibold text-[13px] sm:text-base leading-snug flex-1 ${
                              showWrong ? 'text-red-700 dark:text-red-400' : (showCorrect || highlightCorrect) ? 'text-emerald-700 dark:text-emerald-400' : isSelected ? 'text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-300'
                            }`}>
                              {optionText}
                            </span>
                            {showWrong && (
                              <span className="text-red-600 font-bold text-xs sm:text-sm whitespace-nowrap">
                                {getText(TEXTS.incorrect)}
                              </span>
                            )}
                            {(showCorrect || highlightCorrect) && (
                              <span className="text-emerald-600 font-bold text-xs sm:text-sm whitespace-nowrap">
                                {getText(TEXTS.correct)}
                              </span>
                            )}
                          </div>
                        </button>
                        {isTTSSupported() && optionText && (
                          <SpeakButton
                            variant="option"
                            phase={speakingId === `o-${currentQuestion.id}-${displayIdx}` ? speechPhase : 'idle'}
                            onClick={() => handleSpeak(`o-${currentQuestion.id}-${displayIdx}`, optionText)}
                            label={i18n.language === 'ar' ? 'استمع للخيار' : i18n.language === 'sv' ? 'Lyssna på alternativet' : 'Listen to option'}
                            stopLabel={i18n.language === 'ar' ? 'إيقاف' : i18n.language === 'sv' ? 'Stoppa' : 'Stop'}
                            testId={`speak-option-${displayIdx}`}
                          />
                        )}
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Show explanation immediately when any answer is selected */}
              {answers[currentQuestion.id] !== undefined && currentQuestion.explanation && (currentQuestion.explanation[i18n.language] || currentQuestion.explanation.en) && (
                <div className={`mt-4 sm:mt-6 p-4 sm:p-5 border-2 rounded-xl sm:rounded-2xl animate-in fade-in slide-in-from-top-2 duration-300 ${
                  wrongAnswers[currentQuestion.id] !== undefined 
                    ? 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20 border-amber-300 dark:border-amber-700' 
                    : 'bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border-emerald-300 dark:border-emerald-700'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      wrongAnswers[currentQuestion.id] !== undefined ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}>
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div>
                      <h4 className={`font-bold mb-2 text-sm sm:text-base ${
                        wrongAnswers[currentQuestion.id] !== undefined ? 'text-amber-800 dark:text-amber-300' : 'text-emerald-800 dark:text-emerald-300'
                      }`}>
                        💡 {getText(TEXTS.explanation)}:
                      </h4>
                      <p className={`text-sm sm:text-base leading-relaxed ${
                        wrongAnswers[currentQuestion.id] !== undefined ? 'text-amber-900 dark:text-amber-200' : 'text-emerald-900 dark:text-emerald-200'
                      }`}>
                        {currentQuestion.explanation[i18n.language] || currentQuestion.explanation.en}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Buttons - Mobile Optimized */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t-2 border-slate-100 dark:border-slate-700">
                {/* Previous Button */}
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => {
                    setCurrentIndex(Math.max(0, currentIndex - 1));
                    setTimeout(() => scrollToQuestion(), 50);
                  }}
                  disabled={currentIndex === 0}
                  className="w-full sm:w-auto gap-2 px-4 sm:px-6 py-3 text-sm sm:text-base order-2 sm:order-1"
                  data-testid="prev-btn"
                >
                  <ArrowLeft className={`w-4 h-4 sm:w-5 sm:h-5 ${isRTL ? 'rotate-180' : ''}`} />
                  <span className="font-semibold"><T k="tests.previous" /></span>
                </Button>

                {/* Question Counter + Report Button */}
                <div className="flex items-center gap-3 order-1 sm:order-2">
                  <div className="text-sm sm:text-base font-bold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 px-4 py-2 rounded-full">
                    {currentIndex + 1} / {questions.length}
                  </div>
                  {/* زر الإبلاغ عن خطأ */}
                  <button
                    onClick={() => {
                      setReportQuestion(currentQuestion);
                      setShowReportModal(true);
                    }}
                    className="p-2 sm:p-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all"
                    title={isRTL ? 'الإبلاغ عن خطأ' : 'Report Error'}
                    data-testid="report-error-btn"
                  >
                    <Flag className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>

                {/* Next/Submit Buttons */}
                <div className="w-full sm:w-auto order-3">
                  {currentIndex === questions.length - 1 ? (
                    <Button
                      size="lg"
                      onClick={handleFinishClick}
                      disabled={loading}
                      className="w-full sm:w-auto gap-2 px-6 sm:px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-sm sm:text-base"
                      data-testid="submit-btn"
                    >
                      <span className="font-bold">{loading ? t('common.loading') : t('tests.submit')}</span>
                    </Button>
                  ) : (
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={handleFinishClick}
                        disabled={loading}
                        className="flex-1 sm:flex-none gap-1 sm:gap-2 px-3 sm:px-4 py-3 border-amber-400 text-amber-600 hover:bg-amber-50 text-sm sm:text-base"
                        data-testid="finish-early-btn"
                      >
                        <span className="font-semibold">
                          {getText(TEXTS.finish)}
                        </span>
                      </Button>
                      <Button
                        size="lg"
                        onClick={() => {
                          setCurrentIndex(Math.min(questions.length - 1, currentIndex + 1));
                          setTimeout(() => scrollToQuestion(), 50);
                        }}
                        disabled={currentIndex === questions.length - 1}
                        className="flex-1 sm:flex-none gap-1 sm:gap-2 px-4 sm:px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-sm sm:text-base"
                        data-testid="next-btn"
                      >
                        <span className="font-semibold"><T k="tests.next" /></span>
                        <ArrowRight className={`w-4 h-4 sm:w-5 sm:h-5 ${isRTL ? 'rotate-180' : ''}`} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Section removed */}
          <div className="hidden">
            
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                  <FileQuestion className="w-7 h-7 text-white" />
                </div>
                <div dir={isRTL ? 'rtl' : 'ltr'}>
                  <h3 className="text-2xl font-black text-slate-900 dark:text-slate-100">
                    {getText(TEXTS.quickNavigation)}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                    {getText(TEXTS.clickAnyQuestion)}
                  </p>
                </div>
              </div>
              
              {/* Big Progress Circle */}
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-xl">
                  <div className="text-center">
                    <div className="text-3xl font-black text-white leading-none">
                      {questions.length > 0 ? Math.round((Object.keys(answers).length / questions.length) * 100) : 0}
                    </div>
                    <div className="text-xs text-emerald-100 font-semibold mt-1">%</div>
                  </div>
                </div>
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white dark:bg-slate-700 px-3 py-1 rounded-full shadow-lg border-2 border-emerald-200 dark:border-emerald-600">
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                    {Object.keys(answers).length}/{questions.length || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Larger, Clearer Grid */}
            <div className="grid grid-cols-5 sm:grid-cols-10 gap-4 mb-6">
              {questions.map((q, idx) => {
                const isAnswered = answers[q.id] !== undefined;
                const isCurrent = idx === currentIndex;
                const isWrong = wrongAnswers[q.id] !== undefined;
                return (
                  <button
                    key={q.id}
                    onClick={() => {
                      setCurrentIndex(idx);
                      setTimeout(() => scrollToQuestion(), 50);
                    }}
                    className={`
                      group relative aspect-square rounded-2xl font-bold text-lg
                      transition-all duration-300 transform
                      ${isCurrent 
                        ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-2xl shadow-blue-500/50 scale-110 ring-4 ring-blue-200 dark:ring-blue-700 z-10' 
                        : isAnswered 
                          ? isWrong
                            ? 'bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg hover:shadow-2xl hover:scale-110 hover:rotate-3'
                            : 'bg-gradient-to-br from-emerald-400 to-emerald-500 text-white shadow-lg hover:shadow-2xl hover:scale-110 hover:rotate-3' 
                          : 'bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-600 text-slate-400 dark:text-slate-300 border-3 border-slate-300 dark:border-slate-500 hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-xl hover:scale-105 hover:text-emerald-600'
                      }
                    `}
                  >
                    <span className="relative z-10 drop-shadow-sm">
                      {idx + 1}
                    </span>
                    
                    {isAnswered && !isCurrent && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-lg">
                        {isWrong 
                          ? <X className="w-4 h-4 text-red-500" />
                          : <CheckCircle className="w-4 h-4 text-emerald-500" />
                        }
                      </div>
                    )}
                    
                    {isCurrent && (
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/20 to-transparent"></div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Clear Legend with Icons */}
            <div className="flex items-center justify-center gap-8 py-4 px-6 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700" dir={isRTL ? 'rtl' : 'ltr'}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                  <span className="text-white text-sm font-bold" data-testid="legend-current">{currentIndex + 1}</span>
                </div>
                <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                  {getText(TEXTS.current)}
                </span>
              </div>
              
              <div className="w-px h-8 bg-slate-300 dark:bg-slate-600"></div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center shadow-lg relative">
                  <span className="text-white text-sm font-bold" data-testid="legend-correct-count">{Object.keys(answers).length - Object.keys(wrongAnswers).length}</span>
                  <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-3 h-3 text-emerald-500" />
                  </div>
                </div>
                <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                  {getText(TEXTS.legendCorrect) || getText(TEXTS.answered)}
                </span>
              </div>
              
              <div className="w-px h-8 bg-slate-300 dark:bg-slate-600"></div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg relative">
                  <span className="text-white text-sm font-bold" data-testid="legend-wrong-count">{Object.keys(wrongAnswers).length}</span>
                  <div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center">
                    <X className="w-3 h-3 text-red-500" />
                  </div>
                </div>
                <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                  {getText(TEXTS.legendWrong) || getText(TEXTS.wrong)}
                </span>
              </div>
              
              <div className="w-px h-8 bg-slate-300 dark:bg-slate-600"></div>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-600 border-3 border-slate-300 dark:border-slate-500 flex items-center justify-center shadow">
                  <span className="text-slate-400 dark:text-slate-300 text-sm font-bold" data-testid="legend-unanswered-count">{questions.length - Object.keys(answers).length}</span>
                </div>
                <span className="text-sm font-bold text-slate-800 dark:text-slate-200">
                  {getText(TEXTS.unanswered)}
                </span>
              </div>
            </div>

            {/* Simple Alert - Only if Unanswered */}
            {Object.keys(answers).length < questions.length && (
              <div className="mt-6 flex items-center gap-4 p-5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/20 border-2 border-amber-300 dark:border-amber-700 rounded-2xl shadow-lg" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-400 flex items-center justify-center shadow-lg">
                  <span className="text-white font-black text-2xl">!</span>
                </div>
                <div className="flex-1">
                  <p className="text-base font-bold text-amber-900 dark:text-amber-300 mb-1">
                    {questions.length - Object.keys(answers).length} {getText(TEXTS.questionsLeft)}
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    {getText(TEXTS.unansweredWillBeWrong).replace('⚠️ ', '')}
                  </p>
                </div>
              </div>
            )}
          </div>

            </div>
            
            {/* Sidebar */}
            <div className="w-full lg:w-80 flex-shrink-0">
              <div className="sticky top-24 space-y-4">
                
                {/* Progress Card */}
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 shadow-xl text-white text-center">
                  <div className="text-5xl font-black mb-2">{questions.length > 0 ? Math.round((Object.keys(answers).length / questions.length) * 100) : 0}%</div>
                  <div className="text-sm opacity-90 font-medium">
                    {Object.keys(answers).length} / {questions.length} {getText(TEXTS.answered)}
                  </div>
                </div>

                {/* Question Grid Card */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 shadow-xl border border-slate-200 dark:border-slate-700">
                  <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-4 text-center" dir={isRTL ? 'rtl' : 'ltr'}>
                    {getText(TEXTS.questionsTitle)}
                  </h4>
                  
                  <div className="grid grid-cols-5 gap-2 mb-4 max-h-96 overflow-y-auto">
                    {questions.map((q, idx) => {
                      const isAnswered = answers[q.id] !== undefined;
                      const isCurrent = idx === currentIndex;
                      const isWrong = wrongAnswers[q.id] !== undefined;
                      return (
                        <button
                          key={q.id}
                          onClick={() => {
                            setCurrentIndex(idx);
                            setTimeout(() => scrollToQuestion(), 50);
                          }}
                          className={`
                            aspect-square rounded-lg font-bold text-xs
                            transition-all duration-200 hover:scale-110
                            ${isCurrent 
                              ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg ring-2 ring-blue-300' 
                              : isAnswered 
                                ? isWrong
                                  ? 'bg-gradient-to-br from-red-500 to-red-600 text-white shadow-md'
                                  : 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md' 
                                : 'bg-slate-100 dark:bg-slate-600 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-500'
                            }
                          `}
                        >
                          {idx + 1}
                        </button>
                      );
                    })}
                  </div>

                  {/* Compact Legend */}
                  <div className="flex items-center justify-around pt-3 border-t border-slate-200 dark:border-slate-600">
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-6 h-6 rounded-md bg-gradient-to-br from-blue-500 to-blue-600 shadow flex items-center justify-center text-[10px] font-bold text-white" data-testid="compact-legend-current">{currentIndex + 1}</div>
                      <span className="text-[10px] text-slate-600 dark:text-slate-400">{getText(TEXTS.legendNow)}</span>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-6 h-6 rounded-md bg-gradient-to-br from-emerald-500 to-emerald-600 shadow flex items-center justify-center text-[10px] font-bold text-white" data-testid="compact-legend-correct">{Object.keys(answers).length - Object.keys(wrongAnswers).length}</div>
                      <span className="text-[10px] text-slate-600 dark:text-slate-400">{getText(TEXTS.legendCorrect)}</span>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-6 h-6 rounded-md bg-gradient-to-br from-red-500 to-red-600 shadow flex items-center justify-center text-[10px] font-bold text-white" data-testid="compact-legend-wrong">{Object.keys(wrongAnswers).length}</div>
                      <span className="text-[10px] text-slate-600 dark:text-slate-400">{getText(TEXTS.legendWrong)}</span>
                    </div>
                    <div className="flex flex-col items-center gap-1">
                      <div className="w-6 h-6 rounded-md bg-slate-100 dark:bg-slate-600 border border-slate-300 dark:border-slate-500 flex items-center justify-center text-[10px] font-bold text-slate-500 dark:text-slate-300" data-testid="compact-legend-todo">{questions.length - Object.keys(answers).length}</div>
                      <span className="text-[10px] text-slate-600 dark:text-slate-400">{getText(TEXTS.legendTodo)}</span>
                    </div>
                  </div>
                </div>

                {/* Alert - if unanswered */}
                {Object.keys(answers).length < questions.length && (
                  <div className="bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-4 shadow">
                    <div className="text-center">
                      <div className="text-2xl font-black text-amber-900 dark:text-amber-300 mb-1">
                        {questions.length - Object.keys(answers).length}
                      </div>
                      <div className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                        {getText(TEXTS.questionsLeft)}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* عرض الأسئلة المجانية المتبقية للمستخدم التجريبي.
                    - على اختبار تاكسي: نعرض 10 - answered_taxi (لكل قسم 10)
                    - على اختبار B: نعرض 20 - answered_b
                    نخفي البطاقة إذا:
                    - عنده اشتراك تاكسي نشط ونحن على اختبار تاكسي
                    - أو الحد كبير جداً (>1000 = غير محدود). */}
                {(() => {
                  const isTaxiTest = (selectedTest?.licenseType || currentQuestion?.licenseType) === 'taxi';
                  // Hide when the current-license quota isn't relevant.
                  if (isTaxiTest && user?.taxi_subscription_status === 'active') return null;
                  if (!isTaxiTest && user?.subscription_status === 'active') return null;
                  if ((user?.questions_limit || 30) > 1000) return null;
                  if (user?.subscription_status !== 'trial' && user?.subscription_status !== 'expired' && !isTaxiTest) return null;

                  const limit = isTaxiTest ? 10 : 20;
                  const used = isTaxiTest
                    ? (user?.taxi_questions_answered || 0)
                    : (user?.questions_answered || 0);
                  const remaining = Math.max(0, limit - used);
                  return (
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border-2 border-emerald-300 dark:border-emerald-700 rounded-xl p-4 shadow">
                      <div className="text-center">
                        <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mb-1">
                          {remaining}
                        </div>
                        <div className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                          🎁 {getText(TEXTS.freeQuestions)}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Old navigation - now removed, moved to under options */}
        </div>
      </main>
      <Footer />
      
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 transform transition-all ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                {getText(TEXTS.confirmFinish)}
              </h3>
              <button 
                onClick={() => setShowConfirmDialog(false)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
              </button>
            </div>
            
            {/* Content */}
            <div className="mb-6">
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                {getText(TEXTS.confirmFinishQuestion)}
              </p>
              
              {/* Stats */}
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">
                    {getText(TEXTS.answeredQuestions)}
                  </span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">{Object.keys(answers).length} / {questions.length}</span>
                </div>
                {Object.keys(answers).length < questions.length && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600 dark:text-slate-400">
                      {getText(TEXTS.unansweredQuestions)}
                    </span>
                    <span className="font-bold text-amber-600 dark:text-amber-400">{questions.length - Object.keys(answers).length}</span>
                  </div>
                )}
              </div>
              
              {Object.keys(answers).length < questions.length && (
                <p className="text-amber-600 dark:text-amber-400 text-sm mt-3 font-medium">
                  {getText(TEXTS.unansweredWillBeWrong)}
                </p>
              )}
            </div>
            
            {/* Buttons */}
            <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button
                variant="outline"
                onClick={() => setShowConfirmDialog(false)}
                className="flex-1 py-3"
              >
                {getText(TEXTS.cancel)}
              </Button>
              <Button
                onClick={confirmFinishTest}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              >
                {loading ? getText(TEXTS.submitting) : getText(TEXTS.yesFinishTest)}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Test Warning Dialog */}
      {showLeaveDialog && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform transition-all ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-red-600">
                {getText(TEXTS.warning)}
              </h3>
              <button 
                onClick={() => {
                  setShowLeaveDialog(false);
                  setPendingNavigation(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            {/* Content */}
            <div className="mb-6">
              <p className="text-slate-700 mb-4 font-medium">
                {i18n.language === 'ar' 
                  ? 'أنت في منتصف الاختبار! إذا غادرت الآن، ستفقد كل تقدمك.' 
                  : i18n.language === 'sv' 
                    ? 'Du är mitt i provet! Om du lämnar nu förlorar du alla dina framsteg.' 
                    : 'You are in the middle of a test! If you leave now, you will lose all your progress.'}
              </p>
              
              {/* Current Progress */}
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">
                    {getText(TEXTS.currentProgress)}
                  </span>
                  <span className="font-bold text-slate-800">{Object.keys(answers).length} / {questions.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-600">
                    {getText(TEXTS.timeRemaining)}
                  </span>
                  <span className="font-bold text-slate-800">
                    {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Buttons */}
            <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Button
                onClick={() => {
                  setShowLeaveDialog(false);
                  // تتبع مغادرة الاختبار
                  if (testTrackingId) {
                    const timeSpent = testStartTimeRef.current ? Math.round((Date.now() - testStartTimeRef.current) / 1000) : null;
                    try {
                      navigator.sendBeacon?.(`${API}/analytics/test-complete`,
                        new Blob([JSON.stringify({ test_session_id: testTrackingId, status: 'abandoned', answers_count: Object.keys(answers).length, time_spent_seconds: timeSpent })], {type: 'application/json'})
                      );
                    } catch { /* silent */ }
                    setTestTrackingId(null);
                  }
                  if (pendingNavigation) {
                    navigate(pendingNavigation);
                  }
                  setPendingNavigation(null);
                }}
                variant="destructive"
                className="flex-1 min-w-0 py-3 px-2 whitespace-normal break-words text-sm sm:text-base h-auto leading-tight"
              >
                {getText(TEXTS.leaveTest)}
              </Button>
              <Button
                onClick={() => {
                  setShowLeaveDialog(false);
                  setPendingNavigation(null);
                }}
                className="flex-1 min-w-0 py-3 px-2 whitespace-normal break-words text-sm sm:text-base h-auto leading-tight bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              >
                {getText(TEXTS.stayAndContinue)}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Subscribe Modal */}
      <SubscribeModal />

      {/* Auth Prompt Modal — forces guests to register/login before starting the free test */}
      <AuthPromptModal />
      <RegisterPromptModal />
      
      {/* Report Error Modal */}
      <ReportModal />

      {/* Google Review Prompt — appears after 3 completed tests */}
      <GoogleReviewPrompt
        open={reviewPrompt.open}
        onClose={reviewPrompt.close}
        googleReviewUrl={reviewPrompt.googleReviewUrl}
        score={reviewPrompt.score}
        ar={i18n.language === 'ar'}
      />
    </div>
  );
};

export default TestsPage;
